import {
	_deepClone,
	capFirstChar,
	convert,
	getLimitedValue,
	kelvin_to_rgb,
	mathEpsilon,
} from './utilities';

import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { getBoostState } from '../helpers/trv_helpers';
import { getExtSensorsStatus } from './ext_sensor_helpers';
import {
	get_gen_2_component_channel_structure,
	is_gen_2,
	hasTilt,
	isBluTRV,
	getBluetoothMacFromId,
	isPlusUni,
	isActuator as isActuatorHelper,
	isNoRelayPlug,
	isYihao,
} from './interface_helpers';
import { devices_status } from 'src/stores/device-runtime/status';
import { convertDateFormat, getTimeFormat } from './date_helper';
import { getVComponentStatus } from './virtual_helpers/virtual_components_status_helpers';
import { isXT1ComponentDevice, isXT1Module } from './xt1_helpers/template_helpers';
import { getXT1TemplateStatus } from './xt1_helpers/template_status_helpers';
import { isVirtualDevice } from './virtual_helpers/virtual_components_helpers';
import { getPeripheralDeviceStatus, isPeripheralDevice } from './addons/addon_device_helpers';
import { getAddonStatus, getPlusUniCountStatus, hasAddon } from 'src/helpers/addons/addon_helpers';
import { getBluTRVStatus } from './virtual_helpers/blutrv_helpers';
import {
	em3_g1_status_configurator,
	em_g1_status_configurator,
	emeter_status_configurator,
} from './emeters_data_helpers';
import { loqedStatusConfigurator } from './loqed_helpers';
import { getVialFaultError, translateVialFaultError } from './notifications/device_notifications_helper';
import type { Device } from 'src/types/Device';
import { filterEnabledObserverIds, getAvaliableBleGateways } from './ble_devices_helpers';
import { cachedLists } from 'src/stores/device-runtime/firmware-ui';
import { location } from 'svelte-spa-router';
import { weather_station_status_configurator } from '../components/WeatherStation/weather_station_helpers';
import { getPillDeviceStatus } from './thepill/thepill_data_helpers';
import { convertAndFormatDistance, garageStates, getGarageIconStatus } from './distance_helper';
import { isNightModeEnabled } from './night_mode_helpers';
import { buildInputInterfaceData } from './shelly_1l_helpers';
import { buildS4Pb0916176901StatusLabelList } from './ventora_fan_device_status_helpers';
import type { UpdateModeTypes } from 'src/stores/ui/edit_lists';
import { getCameraStatusLabelList } from 'src/components/Camera/helpers/camera-helpers';
import { showNewLayout as showNewLayoutStore } from 'src/stores/toDelete/new_layout';
import { getSoilSensorDeviceStatus, isBluSoilSensor } from './blu_soil_sensor/blu_soil_sensor_helpers';
import {
	getLiquidLevelSensorDeviceStatus,
	isBluLiquidLevelSensor,
} from './blu_liquid_level_sensor/blu_liquid_level_sensor_helpers';

let _format;
format.subscribe((e) => (_format = e));

export const lux_value = (status, settings, device) => {
	let text;

	if (
		typeof status !== 'undefined' ||
		typeof settings !== 'undefined' ||
		typeof device !== 'undefined'
	) {
		const value = status?.lux?.value;
		const dark = settings?.dark_threshold;
		const dusk = settings?.twilight_threshold;
		const illumination = settings?.illumination;

		if (typeof illumination !== 'undefined') {
			text = _format(illumination);
		} else if (dark >= 0 && dusk > 0) {
			if (value <= dark) {
				text = _format('alarm_state_dark', { default: 'Dark' });
			} else if (value > dark && value < dusk) {
				text = _format('alarm_state_twilight', { default: 'Twilight' });
			} else if (value >= dusk) {
				text = _format('alarm_state_bright', { default: 'Bright' });
			}
			//    console.log("from settings")
			// https://en.wikipedia.org/wiki/Lux using this link to convert lux to text
		} else if (device?.type === 'SHSEN-1') {
			if (status.lux.value < 100) {
				text = _format('alarm_state_dark', { default: 'Dark' });
			} else if (status.lux.value > 100 && status.lux.value < 10000) {
				text = _format('alarm_state_twilight', { default: 'Twilight' });
			} else if (status.lux.value >= 10000) {
				text = _format('alarm_state_bright', { default: 'Bright' });
			}
			// } else {
			// 	text =
			// 		typeof status?.lux?.illumination !== 'undefined'
			// 			? _format(status?.lux?.illumination)
			// 			: isNaN(status?.lux?.value)
			// 			? 'UNKNOWN'
			// 			: Number(status?.lux?.value) + ' LUX' || status?.lux?.illumination;
		}
	}
	return text;
};

const extractLuxValueWallDisplay = (status, settings) => {
	const value = status['illuminance:0']?.lux;
	const dark = settings['illuminance:0']?.dark_thr;
	const bright = settings['illuminance:0']?.bright_thr;
	const illumination = status['illuminance:0']?.illumination;

	let text;

	if (typeof illumination !== 'undefined' && illumination !== '') {
		//Pako- added for second channels https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/work_items/2919
		text = _format('alarm_state_' + illumination);
	} else if (dark >= 0 && bright > 0) {
		if (value <= dark) {
			text = _format('alarm_state_dark', { default: 'dark' });
		} else if (value > dark && value < bright) {
			text = _format('alarm_state_twilight', { default: 'twilight' });
		} else if (value >= bright) {
			text = _format('alarm_state_bright', { default: 'bright' });
		}
	}
	return text;
};

const extractTemperatureValueWallDisplay = (status, externalSensor = false) => {
	const temperature = status['temperature:0']?.tC;
	const externalTemperature = status['thermostat:0']?.current_C;
	return mathEpsilon(externalSensor ? externalTemperature : temperature) + ' °C'; //visualizing current temperature based on https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/1983 or fallback to temperature sensor value
};

const extractHumidityValueWallDisplay = (status) => {
	return mathEpsilon(status?.['humidity:0']?.rh) + ' %';
};

export const status_configurator = (
	status,
	device,
	settings?,
	inPreview = false,
	showNewLayout = get(showNewLayoutStore),
) => {
	const normalizeForLayout = (data) =>
		showNewLayout && Array.isArray(data) ? normalizeLabelListIconsToLight(data) : data;

	if (isBluTRV(device?.type)) {
		return normalizeForLayout(getBluTRVStatus(device));
	}
	if (isPeripheralDevice(device?.type)) {
		return normalizeForLayout(getPeripheralDeviceStatus(device));
	}
	if (isVirtualDevice(device?.type)) {
		return normalizeForLayout(getVComponentStatus(device));
	}
	if (isXT1Module(device?.type) && !isXT1ComponentDevice(device)) {
		return normalizeForLayout(getAddonStatus(device, getXT1TemplateStatus(device.id), false, true));
	}
	if (isBluSoilSensor(device?.type)) {
		return normalizeForLayout(getSoilSensorDeviceStatus(device));
	}
	if (isBluLiquidLevelSensor(device?.type)) {
		return normalizeForLayout(getLiquidLevelSensorDeviceStatus(device));
	}

	let interface_data = [];
	let valueFormat = 0; // use for toFixed(valueFormat) if F should be 0 if C should be 2

	if (
		typeof status === 'undefined' ||
		typeof device === 'undefined' ||
		typeof settings === 'undefined'
	) {
		return normalizeForLayout(interface_data);
	}
	try {
		const structure = get_gen_2_component_channel_structure(device);
		switch (device.type) {
			//#region Gen 1
			case 'SHEM-3':
				interface_data = em3_g1_status_configurator(status);
				break;
			case 'SHEM':
				interface_data = em_g1_status_configurator(status, device);
				break;
			case 'SHSW-1S':
			case 'SHSW-PM':
			case 'SHPLG-1':
			case 'SHPLG-S':
			case 'SHPLG2-1':
			case 'SHPLG-U1':
				interface_data.push({
					name: 'power',
					icon: 'fas fa-bolt',
					value:
						status.relays[device.channel].ison && status.meters?.[device.channel]?.power
							? convert(status.meters[device.channel].power, 'W')
							: 0 + ' W',
				});

				if (status.relays[device.channel].voltage) {
					interface_data.push({
						name: 'voltage',
						icon: 'fas fa-circle-bolt',
						value: status.relays[device.channel].voltage + ' V',
					});
				}

				interface_data = getExtSensorsStatus(device, interface_data);
				if (settings?.ext_switch_enable) {
					interface_data = extract_ext_switch_status(device, status, settings, interface_data);
				}

				break;

			case 'SHSW-1':
			case 'SHSW-L':
				if (settings.power > -1) {
					interface_data.push({
						name: 'power',
						icon: 'fas fa-bolt',
						value: status.relays[device.channel].ison
							? convert(settings.power, 'W')
							: 0 + ' W',
					});
				}

				interface_data = getExtSensorsStatus(device, interface_data);
				if (settings?.ext_switch_enable) {
					interface_data = extract_ext_switch_status(device, status, settings, interface_data);
				}
				break;

			case 'SHSW-21':
			case 'SHSW-25':
			case 'SHSW-44':
				if (device.category === 'roller') {
					if (typeof status.rollers === 'undefined') break;
					// interface_data = [
					//     {name:'power', icon:'fas fa-bolt', value:convert(status.rollers[0].power,'W')},
					// ];
					const position = Number(status.rollers[0].current_pos);
					if (position != 101 && position != 112 && position < 101) {
						interface_data.push({
							name: 'blinds',
							icon: position > 0 ? 'fa fa-blinds-raised' : 'fa fa-blinds',
							value: position + ' %',
						});
					}
					interface_data.push({
						name: 'power',
						icon: 'fas fa-bolt',
						value: convert(status.rollers[0].power, 'W'),
					});
				} else {
					if (typeof status.meters[device.channel] !== 'undefined') {
						interface_data = [
							{
								name: 'power',
								icon: 'fas fa-bolt',
								value: convert(status.meters[device.channel].power, 'W'),
							},
						];
					} else {
						// todo: think of a better way to handle this case
						interface_data = [
							{
								name: 'power',
								icon: 'fas fa-bolt',
								value: convert(status.meters[0].power, 'W'),
							},
						];
					}
				}
				if (typeof status.voltage !== 'undefined') {
					interface_data.push({
						name: 'voltage',
						icon: 'fas fa-circle-bolt',
						value: (status.voltage ?? 0) + ' V',
					});
				}
				break;

			case 'SH2LED-1':
				interface_data = [
					{
						name: 'brightness',
						icon: 'fa-light fa-sun-bright',
						value: status.lights[device.channel].brightness + ' %',
					},
				];
				break;

			case 'SHDIMW-1':
				interface_data = [
					{
						name: 'brightness',
						icon: 'fa-light fa-sun-bright',
						value: status.lights[device.channel].brightness + ' %',
					},
				];
				break;

			case 'SHBDUO-1':
				//adding white channel to labels for DUO
				let rgb = kelvin_to_rgb(status.lights[device.channel].temp);
				interface_data = [
					{
						name: 'color_temp',
						icon: 'fa-duotone fa-light fa-circle',
						iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${rgb[0]},${rgb[1]},${rgb[2]}); --fa-secondary-opacity: 1`,
						value:
							typeof status.lights[device.channel]?.temp === 'number'
								? `${status.lights[device.channel].temp} K`
								: '',
					},
					{
						name: 'power',
						icon: 'fas fa-bolt',
						value:
							status.lights[device.channel].ison && status.meters?.[device.channel]?.power
								? convert(status.meters[device.channel].power, 'W')
								: 0 + ' W',
					},
					{
						name: 'brightness',
						icon: 'fa-light fa-sun-bright',
						value: status.lights[device.channel].ison
							? status.lights[device.channel].brightness + ' %'
							: 0 + ' %',
					},
				];
				break;
			case 'SHDM-1':
			case 'SHDM-2':
			case 'SHVIN-1':
				interface_data = [
					{
						name: 'power',
						icon: 'fas fa-bolt',
						value:
							status.lights[device.channel].ison && status.meters?.[device.channel]?.power
								? convert(status.meters[device.channel].power, 'W')
								: 0 + ' W',
					},
					{
						name: 'brightness',
						icon: 'fa-light fa-sun-bright',
						value: status.lights[device.channel].ison
							? status.lights[device.channel].brightness + ' %'
							: 0 + ' %',
					},
				];
				break;

			case 'SHRGBW2':
				//console.log('device ID ', device.id)
				//console.log('device NAME', device.name)
				//console.log('settings', settings)

				//console.log('status', status)
				if (status.lights[device.channel]?.mode === 'white') {
					interface_data = [
						//removing white mode from rgbw (Bobby didn't like it)
						{
							name: 'power',
							icon: 'fas fa-bolt',
							value: status.lights[device.channel].ison
								? convert(status.meters[device.channel].power, 'W')
								: 0 + ' W',
						},
						{
							name: 'brightness',
							icon: 'fa-light fa-sun-bright',
							value:
								(status.lights[device.channel].ison
									? status.lights[device.channel].brightness
									: 0) + ' %',
						},
					];
				} else {
					interface_data = [
						{
							name: 'color',
							icon: 'fa-duotone fa-light fa-circle',
							iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${status.lights[device.channel]?.red},${status.lights[device.channel]?.green},${status.lights[device.channel]?.blue}); --fa-secondary-opacity: 1`,
						},
						{
							name: 'power',
							icon: 'fas fa-bolt',
							value: status.lights[device.channel]?.ison
								? convert(status.meters[device.channel]?.power, 'W')
								: 0 + ' W',
						},
						{
							name: 'brightness',
							icon: 'fa-light fa-sun-bright',
							value:
								(status.lights[device.channel]?.ison
									? status.lights[device.channel]?.gain
									: 0) + ' %',
						},
					];
				}

				break;
			case 'SHCB-1':
			case 'SHBLB-1':
			case 'SHCL-255':
				if (status.lights[device.channel]?.mode === 'white') {
					const temp = status.lights[device.channel]?.temp;
					const tempRgb = kelvin_to_rgb(temp);
					interface_data = [
						//this is fix to visualize white mode correctly
						{
							name: 'color_temp',
							icon: 'fa-duotone fa-light fa-circle',
							iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${tempRgb[0]},${tempRgb[1]},${tempRgb[2]}); --fa-secondary-opacity: 1`,
							value: typeof temp === 'number' ? `${temp} K` : '',
						},
						{
							name: 'power',
							icon: 'fas fa-bolt',
							value:
								status?.lights?.[device.channel]?.ison &&
								status?.meters?.[device.channel]?.power
									? convert(status.meters[device.channel].power, 'W')
									: 0 + ' W',
						},
						{
							name: 'brightness',
							icon: 'fa-light fa-sun-bright',
							value:
								(status.lights[device.channel]?.ison
									? status.lights[device.channel].brightness
									: 0) + ' %',
						},
					];
				} else {
					interface_data = [
						{
							name: 'color',
							icon: 'fa-duotone fa-light fa-circle',
							iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${status.lights[device.channel]?.red},${status.lights[device.channel]?.green},${status.lights[device.channel].blue}); --fa-secondary-opacity: 1`,
						},
						{
							name: 'power',
							icon: 'fas fa-bolt',
							value:
								status?.lights?.[device.channel]?.ison &&
								status?.meters?.[device.channel]?.power
									? convert(status.meters[device.channel].power, 'W')
									: 0 + ' W',
						},
						{
							name: 'brightness',
							icon: 'fa-light fa-sun-bright',
							value:
								(status.lights[device.channel]?.ison
									? status.lights[device.channel].gain
									: 0) + ' %',
						},
					];
				}

				break;

			// Shelly Gas
			case 'SHGS-1':
				interface_data = [
					{
						name: 'concentration',
						icon: 'fas fa-chart-scatter',
						value: status.concentration.ppm,
					},
					{
						name: 'sensor',
						icon:
							status.gas_sensor.alarm_state !== 'none'
								? 'text-danger fa-solid fa-smoke'
								: 'fak fa-light-smoke-slash',
						value:
							status.gas_sensor.alarm_state !== 'none'
								? _format('prop_gas', { default: 'gas' })
								: _format('status_no', { default: 'No' }),
						main: true,
					},
				];
				if (typeof status._updated !== 'undefined') {
					interface_data.push(format_last_seen_report(status));
				}
				if (typeof status.valves !== 'undefined') {
					if (status.valves[0].state !== 'not_connected') {
						let i18n = status.valves[0].state.match('ing')
							? status.valves[0].state
							: status.valves[0].state.match('opened')
								? 'open'
								: 'close';
						interface_data.push({
							name: 'valves',
							icon: i18n === 'open' ? 'fak fa-light-valve' : 'fak fa-solid-valve',
							value: _format(
								`alarm_state_state_${i18n}${status.valves[0].state.match('opened') ? '' : 'd'}`,
							),
						});
					}
				}
				break;

			// Shelly Smoke
			case 'SHSM-01':
				interface_data = [
					{
						name: 'temperature',
						icon: 'fa fa-thermometer-three-quarters',
						value: status?.tmp?.value + ' °' + status?.tmp?.units,
					},
					{
						name: 'smoke',
						icon: status.smoke
							? 'text-danger fa-solid fa-fire-smoke'
							: 'fak fa-light-fire-smoke-slash-tr',
						value: status.smoke
							? _format('smoke_detected')
							: _format('alarm_state_detected_no'),
						main: true,
					},
				];
				/** comment this, because the FW fix does not cover this device
				 * we have not any information is this device on production or just prototype,
				 * so skip this for now
				 */
				// if(status?.sensor_error && Number(status?.sensor_error) > 0){
				// 	interface_data.push({
				// 		icon: 'fa fa-sensor-alert', value: 'ERR', main: true
				// 	});
				// }
				if (typeof status?._updated !== 'undefined') {
					interface_data.push(format_last_seen_report(status));
				}
				break;

			// Shelly Sense
			case 'SHSEN-1':
				valueFormat = status?.tmp?.units === 'F' ? 0 : 1;
				interface_data = [
					{
						name: 'humidity',
						icon: 'fa fa-humidity',
						value: mathEpsilon(status?.hum?.value) + ' %',
					},
					{
						name: 'temperature',
						icon: 'fa fa-thermometer-three-quarters',
						value: `${mathEpsilon(status?.tmp?.value).toFixed(valueFormat)} ${
							status?.tmp?.units === 'C' ? ' °C' : ' °F'
						}`,
					},
					// {icon:'fa fa-sun', value:mathEpsilon(status.lux.value) + ' lux'},
					{
						name: 'lux',
						icon: 'fa fa-sun',
						value: lux_value(status, settings, device),
					},
					{
						name: 'motion',
						icon: status.motion ? 'text-primary fa fa-person-walking' : 'fas fa-person',
						value: status.motion ? _format('motion') : _format('status_no'),
						main: true,
					},
				];
				if (typeof status._updated !== 'undefined') {
					interface_data.push(format_last_seen_report(status));
				}
				break;

			// Shelly H&T
			case 'SHHT-1':
				/** REQUIRED: FW v1.11.8-rc2
				 * 1. The app behavior with old FW versions
				 * - all older FW versions report with broken status package, when error occurs
				 * (sensor_error comes, but all other properties miss,
				 * then sensor again evaluate data properly and
				 * returns properties via socket,
				 * but never clears the sensor_error (in fact the device does not send it anymore),
				 * SO here comes cloud issue,
				 * where the sensor_error is stored (on the first time detecting),
				 * and even if device reports with valid data
				 * cloud returns all properties + sensor_error)
				 * AND we don't know what to show, so we still show error.
				 * In this case I am expecting the user report to the support for sensor error,
				 * so we could ask them to update their devices to current FW version
				 * 2. The app behaivior with new FW
				 * - WITH the v1.11.8-rc2 the missing properties issue is fixed, so
				 * even if error occurs, we show it, then we clean it, when the sensor_error cleans
				 */
				// if (
				// 	(typeof status.is_valid !== 'undefined' && !status.is_valid) ||
				// 	(status?.sensor_error && Number(status?.sensor_error) > 0)
				// ) {
				// 	interface_data = [
				// 		{
				// 			icon: 'fa fa-sensor-alert',
				// 			value: 'ERR',
				// 			main: true
				// 		}
				// 	];
				// } else {

				valueFormat = status?.tmp?.units === 'F' ? 0 : 1;
				interface_data = [
					{
						name: 'humidity',
						icon: 'fa fa-humidity',
						value:
							status?.hum?.value == null || status?.hum?.value === 999
								? 'ERR'
								: mathEpsilon(status?.hum?.value, 1) + ' %',
						iconColor: `${
							status?.hum?.value == null || status?.hum?.value === 999
								? '--color-danger'
								: ''
						}`,
					},
					{
						name: 'temperature',
						icon: 'fa fa-thermometer-three-quarters',
						value:
							status.tmp.value == null || status.tmp.value === 999
								? 'ERR'
								: `${mathEpsilon(status.tmp.value, 1).toFixed(valueFormat)}${
										status?.tmp?.units === 'C' ? ' °C' : ' °F'
									}`,
						iconColor: `${status.tmp.value == null || status.tmp.value === 999 ? '--color-danger' : ''}`,
						main: true,
						big_val: true,
					},
				];
				// }

				if (
					status?.bat?.value === -1 ||
					(settings.hasOwnProperty('external_power') && settings.external_power == 1)
				) {
					interface_data.push({
						name: 'battery',
						icon: 'fa fa-plug',
						value: _format('ext_power_hnt'),
					});
				} else {
					interface_data.push({
						name: 'battery',
						icon: 'fa-duotone fa-light fa-battery-full',
						value: status?.bat?.value == null ? 'ERR' : status?.bat?.value + ' %',
						iconStyle: getBatteryIconStyle(status?.bat?.value),
					});
				}
				// }

				/** always check for last report even if there is an error */
				if (typeof status._updated !== 'undefined') {
					interface_data.push(format_last_seen_report(status));
				}
				// if (typeof status._sleeping !== 'undefined') {
				//     interface_data.push(
				//         {name:'sleeping', icon:faSnooze, value:status._sleeping?'Sleeping':'Awake'},
				//     )
				// }
				break;

			// Shelly Door/Window 1
			case 'SHDW-1':
				/** REQUIRED: FW v1.11.8-rc2
				 * 1. The app behavior with old FW versions
				 * - all older FW versions report with broken status package, when error occurs
				 * (sensor_error comes, but all other properties miss,
				 * then sensor again evaluate data properly and
				 * returns properties via socket,
				 * but never clears the sensor_error (in fact the device does not send it anymore),
				 * SO here comes cloud issue,
				 * where the sensor_error is stored (on the first time detecting),
				 * and even if device reports with valid data
				 * cloud returns all properties + sensor_error)
				 * AND we don't know what to show, so we still show error.
				 * In this case I am expecting the user report to the support for sensor error,
				 * so we could ask them to update their devices to current FW version
				 * 2. The app behaivior with new FW
				 * - WITH the v1.11.8-rc2 the missing properties issue is fixed, so
				 * even if error occurs, we show it, then we clean it, when the sensor_error cleans
				 */
				/**
				 * [07-03-2022] Change sensor_error check -> FW 1.11.8 still shows error but with valid data
				 * use isValid prop
				 * - if still goes wrong -> remove check and show broken data :))
				 */
				// if (
				// 	(typeof status.is_valid !== 'undefined' && !status.is_valid) ||
				// 	(status?.sensor_error && Number(status?.sensor_error) > 0)
				// ) {
				// 	interface_data = [
				// 		{
				// 			icon: 'fa fa-sensor-alert',
				// 			value: 'ERR',
				// 			main: true
				// 		}
				// 	];
				// } else {
				interface_data = [
					{
						name: 'door',
						icon:
							status?.sensor?.state === 'open'
								? 'text-primary fa fa-door-open'
								: 'fa fa-door-closed',
						value: _format(
							`alarm_state_state_${status?.sensor?.state}${status?.sensor?.state === 'open' ? '' : 'd'}`,
						),
						main: true,
					},
					// {name:'lux', icon:'fa fa-sun', value:status.lux.value},
					{
						name: 'lux',
						icon: 'fa fa-sun',
						value: lux_value(status, settings, device),
					},
					{
						name: 'battery',
						icon: 'fa-duotone fa-light fa-battery-full',
						value: status?.bat?.value == null ? 'ERR' : status?.bat?.value + ' %',
						iconStyle: getBatteryIconStyle(status?.bat?.value),
					},
				];
				// }
				/** always check for last report time even if there is error */
				if (typeof status._updated !== 'undefined') {
					interface_data.push(format_last_seen_report(status));
				}
				// if (typeof status._sleeping !== 'undefined') {
				//     interface_data.push(
				//         {name:'sleeping', icon:faSnooze, value:status._sleeping?'Sleeping':'Awake'},
				//     )
				// }
				break;

			// Shelly Door/Window 2
			case 'SHDW-2':
				/** REQUIRED: FW v1.11.8-rc2
				 * 1. The app behavior with old FW versions
				 * - all older FW versions report with broken status package, when error occurs
				 * (sensor_error comes, but all other properties miss,
				 * then sensor again evaluate data properly and
				 * returns properties via socket,
				 * but never clears the sensor_error (in fact the device does not send it anymore),
				 * SO here comes cloud issue,
				 * where the sensor_error is stored (on the first time detecting),
				 * and even if device reports with valid data
				 * cloud returns all properties + sensor_error)
				 * AND we don't know what to show, so we still show error.
				 * In this case I am expecting the user report to the support for sensor error,
				 * so we could ask them to update their devices to current FW version
				 * 2. The app behaivior with new FW
				 * - WITH the v1.11.8-rc2 the missing properties issue is fixed, so
				 * even if error occurs, we show it, then we clean it, when the sensor_error cleans
				 */
				/**
				 * [07-03-2022] Change sensor_error check -> FW 1.11.8 still shows error but with valid data
				 * use isValid prop
				 * - if still goes wrong -> remove check and show broken data :))
				 */

				// if (
				// 	(typeof status.is_valid !== 'undefined' && !status.is_valid) ||
				// 	(status?.sensor_error && Number(status?.sensor_error) > 0)
				// ) {
				// 	interface_data = [
				// 		{
				// 			icon: 'fa fa-sensor-alert',
				// 			value: 'ERR',
				// 			main: true
				// 		}
				// 	];
				// } else {
				interface_data = [
					{
						name: 'door',
						icon:
							status.sensor?.state === 'open'
								? 'text-primary fa fa-door-open'
								: 'fa fa-door-closed',
						value: _format(
							`alarm_state_state_${status?.sensor?.state}${status?.sensor?.state === 'open' ? '' : 'd'}`,
						),
						main: true,
					},
					// {name:'lux', icon:'fa fa-sun', value:status.lux.value},
					{
						name: 'lux',
						icon: 'fa fa-sun',
						value: lux_value(status, settings, device),
					},
					{
						name: 'battery',
						icon: 'fa-duotone fa-light fa-battery-full',
						value: status?.bat?.value == null ? 'ERR' : status?.bat?.value + ' %',
						iconStyle: getBatteryIconStyle(status?.bat?.value),
					},
				];

				valueFormat = status?.tmp?.units === 'F' ? 0 : 1;
				if (status?.tmp) {
					interface_data.push({
						name: 'temperature',
						icon: 'fa fa-thermometer-three-quarters',
						value:
							status.tmp.value == null || status.tmp.value === 999
								? 'ERR'
								: `${mathEpsilon(status.tmp.value, 1).toFixed(valueFormat)}${
										status?.tmp?.units === 'C' ? ' °C' : ' °F'
									}`,
						iconColor: `${status.tmp.value == null || status.tmp.value === 999 ? '--color-danger' : ''}`,
					});
				}

				if (status?.accel?.tilt > -1) {
					interface_data.push({
						name: 'tilt',
						icon: 'fas fa-ruler-triangle',
						value: status?.accel?.tilt + ' °',
					});
				}

				if (status?.accel?.vibration > -1) {
					interface_data.push({
						name: 'vibration',
						icon: 'fa fa-waveform',
						value:
							status?.accel?.vibration === 0
								? _format('no_vibration')
								: _format('vibration_detected'),
					});
				}
				// }

				/**always check for last report even if it is an error */
				if (typeof status._updated !== 'undefined') {
					interface_data.push(format_last_seen_report(status));
				}

				// if (typeof status._sleeping !== 'undefined') {
				//     interface_data.push(
				//         {name:'sleeping', icon:faSnooze, value:status._sleeping?'Sleeping':'Awake'},
				//     )
				// }

				break;

			// Shelly Flood
			case 'SHWT-1':
				/** REQUIRED: FW v1.11.8-rc2
				 * 1. The app behavior with old FW versions
				 * - all older FW versions report with broken status package, when error occurs
				 * (sensor_error comes, but all other properties miss,
				 * then sensor again evaluate data properly and
				 * returns properties via socket,
				 * but never clears the sensor_error (in fact the device does not send it anymore),
				 * SO here comes cloud issue,
				 * where the sensor_error is stored (on the first time detecting),
				 * and even if device reports with valid data
				 * cloud returns all properties + sensor_error)
				 * AND we don't know what to show, so we still show error.
				 * In this case I am expecting the user report to the support for sensor error,
				 * so we could ask them to update their devices to current FW version
				 * 2. The app behaivior with new FW
				 * - WITH the v1.11.8-rc2 the missing properties issue is fixed, so
				 * even if error occurs, we show it, then we clean it, when the sensor_error cleans
				 */
				/**
				 * [07-03-2022] Change sensor_error check -> FW 1.11.8 still shows error but with valid data
				 * use isValid prop
				 * - if still goes wrong -> remove check and show broken data :))
				 */

				// if (
				// 	(typeof status.is_valid !== 'undefined' && !status.is_valid) ||
				// 	(status?.sensor_error && Number(status?.sensor_error) > 0)
				// ) {
				// 	interface_data = [
				// 		{
				// 			icon: 'fa fa-sensor-alert',
				// 			value: 'ERR',
				// 			main: true
				// 		}
				// 	];
				// } else {
				valueFormat = status?.tmp?.units === 'F' ? 0 : 1;

				interface_data = [
					{
						name: 'flood',
						icon: status?.flood
							? 'text-danger fa fa-house-flood-water'
							: 'fas fa-droplet-slash',
						iconColor: status?.flood ? '--color-danger' : '',
						value: status.flood
							? _format('prop_flood', { default: 'Flood' })
							: _format('status_no', { default: 'No' }),
						main: true,
					},
					{
						name: 'temperature',
						icon: 'fa fa-thermometer-three-quarters',
						value:
							status?.tmp?.value == null || status.tmp.value === 999
								? 'ERR'
								: `${mathEpsilon(status.tmp.value, 1).toFixed(valueFormat)}${
										status?.tmp?.units === 'C' ? ' °C' : ' °F'
									}`,
						iconColor: `${status?.tmp?.value == null || status.tmp.value === 999 ? '--color-danger' : ''}`,
					},
					{
						name: 'battery',
						icon: 'fa-duotone fa-light fa-battery-full',
						value: status?.bat?.value == null ? 'ERR' : status?.bat?.value + ' %',
						iconStyle: getBatteryIconStyle(status?.bat?.value),
					},
				];
				// }

				/**always check for last report even if it was error */
				if (typeof status._updated !== 'undefined') {
					interface_data.push(format_last_seen_report(status));
				}

				// if (typeof status._sleeping !== 'undefined') {
				//     interface_data.push(
				//         {name:'sleeping', icon:faSnooze, value:status._sleeping?'Sleeping':'Awake'},
				//     )
				// }
				break;

			// Shelly Button 1,2
			case 'SHBTN-1':
			case 'SHBTN-2':
				/** REQUIRED: FW v1.11.8-rc2
				 * 1. The app behavior with old FW versions
				 * - all older FW versions report with broken status package, when error occurs
				 * (sensor_error comes, but all other properties miss,
				 * then sensor again evaluate data properly and
				 * returns properties via socket,
				 * but never clears the sensor_error (in fact the device does not send it anymore),
				 * SO here comes cloud issue,
				 * where the sensor_error is stored (on the first time detecting),
				 * and even if device reports with valid data
				 * cloud returns all properties + sensor_error)
				 * AND we don't know what to show, so we still show error.
				 * In this case I am expecting the user report to the support for sensor error,
				 * so we could ask them to update their devices to current FW version
				 * 2. The app behaivior with new FW
				 * - WITH the v1.11.8-rc2 the missing properties issue is fixed, so
				 * even if error occurs, we show it, then we clean it, when the sensor_error cleans
				 */
				/**
				 * It is a little different of the sensors, because whe main component is a FAKE button,
				 * but the behaivior is the same,
				 * if there is an error, battery will be hidden, until the device cleans the error
				 * (if it is a v.1.11.8 at least)
				 */
				/**
				 * [07-03-2022] Change sensor_error check -> FW 1.11.8 still shows error but with valid data
				 * use isValid prop
				 * - if still goes wrong -> remove check and show broken data :))
				 */

				if (
					typeof status.is_valid !== 'undefined' &&
					!status.is_valid &&
					status?.sensor_error &&
					Number(status?.sensor_error) > 0
				) {
					interface_data = [
						{
							name: 'sensor',
							icon: 'fa fa-sensor-alert',
							iconColor: '--color-danger',
							value: 'ERR',
						},
					];
				} else {
					interface_data = [
						{
							name: 'battery',
							icon: 'fa-duotone fa-light fa-battery-full',
							value: status?.bat?.value + ' %',
							iconStyle: getBatteryIconStyle(status?.bat?.value),
						},
					];
				}

				if (typeof status._updated !== 'undefined') {
					interface_data.push(format_last_seen_report(status));
				}
				// if (typeof status._sleeping !== 'undefined') {
				//     interface_data.push(
				//         {icon:faSnooze, value:status._sleeping?'Sleeping':'Awake'},
				//     )
				// }
				break;

			// Shelly Uni
			case 'SHUNI-1':
				interface_data = [
					{
						name: 'power',
						icon: 'fas fa-circle-bolt',
						value: (status.adcs[0].voltage ?? 0) + ' V',
					},
				];

				interface_data = getExtSensorsStatus(device, interface_data);
				break;

			case 'WS1NB':
				interface_data = [
					{ name: 'flood', icon: 'fa fa-sun', value: status.flood },
					{
						name: 'battery',
						icon: 'fa-duotone fa-light fa-battery-full',
						value: status.bat.value + ' %',
						iconStyle: getBatteryIconStyle(status.bat.value),
					},
				];

				break;

			case 'HT1NB':
				interface_data = [
					{
						name: 'humidity',
						icon: 'fa fa-humidity',
						value: status.hum.value + ' %',
					},
					{
						name: 'temperature',
						icon: 'fa fa-thermometer-three-quarters',
						value: status.tmp.value + ' C',
					},
					{
						name: 'battery',
						icon: 'fa-duotone fa-light fa-battery-full',
						value: status.bat.value + ' %',
						iconStyle: getBatteryIconStyle(status.bat.value),
					},
				];
				break;

			//#region (So Called) Half-Sleeping
			// Shelly Motion 1,2
			case 'SHMOS-01':
			case 'SHMOS-02':
				valueFormat = status?.tmp?.units === 'F' ? 0 : 1;

				// // if (status.sensor.motion) {
				//     interface_data.push(
				//         {name:'timer', icon:'fa fa-clock', value:to_hh_mm_ss(status.sensor.timestamp)}
				//     )
				// // }
				if (status?.sensor?.vibration) {
					interface_data.push({
						name: 'vibration',
						icon: 'fa fa-waveform',
						value: _format('vibration_detected'),
					});
				}

				if (typeof status.tmp !== 'undefined') {
					interface_data.push({
						name: 'temperature',
						icon: 'fa fa-thermometer-three-quarters',
						iconColor:
							status?.tmp?.value == null || status.tmp.value === 999
								? '--color-danger'
								: '',
						value:
							settings?.sensors?.temperature_threshold == 0
								? _format('disabled', { default: 'disabled' })
								: status?.tmp?.value == null || status.tmp.value === 999
									? 'ERR'
									: `${status.tmp.value?.toFixed(valueFormat)} ${
											status.tmp.units == 'C' ? ' °C' : ' °F'
										}`,
					});
				}

				if (!status.sensor.motion) {
					if (status.sensor.timestamp === 0) {
						interface_data.push({
							name: 'last',
							icon: 'fas fa-person',
							value: _format('no_motion_log'),
						});
					} else {
						let date = new Date(status.sensor.timestamp);
						let updated = {
							_updated: date,
						};

						interface_data.push(
							format_last_seen_report(updated, 'fa fa-person-walking text-primary'),
						);
					}
				}

				interface_data.push({
					name: 'motion',
					icon:
						settings?.motion?.enabled && status.sensor.active
							? status.sensor.motion
								? 'text-primary fa fa-person-walking'
								: 'fas fa-person'
							: 'fa fa-eye-slash',
					value:
						settings?.motion?.enabled && status.sensor.active
							? status?.sensor?.motion
								? _format('motion', { default: 'motion' })
								: _format('status_no', { default: 'no' })
							: _format('disabled'),
					main: true,
				});

				if (status?.charger) {
					interface_data.push({
						name: 'battery',
						icon: 'fa fa-plug',
						value: _format('ext_power_hnt'),
					});
				}

				interface_data.push({
					name: 'battery',
					icon: 'fa-duotone fa-light fa-battery-full',
					value: status?.bat?.value + ' %',
					iconStyle: getBatteryIconStyle(status?.bat?.value),
				});

				interface_data.push({
					name: 'lux',
					icon: 'fa fa-sun',
					value: lux_value(status, settings, device),
				});

				// if (status?.charger) {
				// 	interface_data.push({
				// 		type: 'battery',
				// 		icon: faPlug,
				// 		value: _format('ext_power_hnt')
				// 	});
				// }
				// interface_data.push({
				// 	type: 'battery',
				// 	icon:
				// 		status?.bat?.value <= 10
				// 			? faBatteryEmpty
				// 			: status?.bat?.value <= 25
				// 			? faBatteryQuarter
				// 			: faBatteryFull,
				// 	value: status?.bat?.value + ' %',
				// 	color: `${
				// 		status?.bat?.value <= 10
				// 			? 'var(--color_scheme-color_danger)'
				// 			: status?.bat?.value <= 25
				// 			? 'var(--color_scheme-color_warning)'
				// 			: ''
				// 	}`
				// });

				break;

			// Shelly TRV
			case 'SHTRV-01':
				let position = Number(Math.ceil(status?.thermostats?.[0]?.pos));
				let error = position === -1;
				valueFormat = status?.thermostats?.[0]?.tmp?.units === 'F' ? 0 : 1;

				//TRV boost status

				const boostState = getBoostState(device, settings, status);
				const boostStatus =
					(boostState.boostEnabled && boostState.time) || inPreview
						? {
								name: 'boost time',
								icon: 'fas fa-fire',
								type: 'primary',
								value: boostState.time
									? `${boostState.time} ${get(format)('select_units_minutes')}`
									: get(format)('none'),
							}
						: {};

				interface_data = [
					boostStatus,
					{
						name: 'temperature',
						icon: settings?.thermostats[0]?.ext_t?.enabled
							? 'fa fa-thermometer'
							: 'fa fa-thermometer-three-quarters',
						value:
							status?.thermostats?.[0]?.tmp?.value.toFixed(valueFormat) +
							' °' +
							status?.thermostats?.[0]?.tmp?.units,
					},
					{
						name: 'valve position',
						icon: error
							? 'fa fa-exclamation'
							: position === 0
								? 'fa-light fa-pipe-valve'
								: 'fa fa-pipe-valve',
						value: !error ? position + ' %' : 'ERR',
					},
				];
				if (status?.charger) {
					interface_data.push({
						name: 'battery',
						icon: 'fa fa-plug',
						value: _format('ext_power_hnt'),
					});
				} else {
					interface_data.push({
						name: 'battery',
						icon: 'fa-duotone fa-light fa-battery-full',
						value: status?.bat?.value + ' %',
						iconStyle: getBatteryIconStyle(status?.bat?.value),
					});
				}
				break;
			//#endregion (So Called) Half-Sleeping
			//#endregion Gen 1

			//#region Gen 2

			//#region PM devices
			// ShellyPlug US
			case 'SNPL-00116US':
			// ShellyPlug US Gen 4
			case 'S4PL-00116US':
			// ShellyPlug IT
			case 'SNPL-00116IT':
			case 'SNPL-00110IT':
			// Shelly Plus Plug S
			case 'SNPL-00110EU':
			case 'SNPL-00112EU':
			case 'SNPL-10112EU':
			case 'S3PL-10112EU':
			//Shelly Plug S Gen3
			case 'S3PL-00112EU':
			// EcoFlow Plug S
			case 'EF3PL-00112EU':
			//Shelly Outdoor Plug S Gen3
			case 'S3PL-20112EU':
			//Shelly Plug M Gen3
			case 'S3PL-30110EU':
			// Shelly Plug M US Gen3
			case 'S3PL-00115US':
			// Shelly Plug CN Gen3
			case 'S3PL-00110CN':
			// Shelly Power Strip 4 Gen4
			case 'S4PL-00416EU':
			// Shelly Power Strip 4 US Gen4
			case 'S4PL-00415US':
			// Shelly Plus Plug UK
			case 'SNPL-00112UK':
			// EcoFlow Plug UK
			case 'EFNPL-00112UK':
			// Shelly PRO 1 PM
			case 'SPSW-001PE16EU':
			case 'SPSW-101PE16EU':
			case 'SPSW-201PE16EU':
			case 'SPSW-201PE15UL':
			case 'SPSW-202PE12UL': // Shelly PRO 2 PM UL
			case 'SPSW-001PE40EU': // Shelly PRO 1 PM G4
			// Shelly PRO 2 PM
			case 'SPSW-002PE16EU':
			case 'SPSW-102PE16EU':
			case 'SPSW-202PE16EU':
			case 'SPSW-202PE16UL':
			// Shelly PRO 4 PM
			case 'SPSW-004PE16EU':
			case 'SPSW-104PE16EU':
			case 'SPSW-204PE16EU':
			// Shelly Plus 1 PM
			case 'SNSW-001P16EU':
			// Shelly Plus 2 PM
			case 'SNSW-002P16EU':
			case 'SNSW-102P16EU':
			// Shelly Plus 1 UL
			case 'SNSW-001P15UL':
			// Shelly 1 PM Mini
			case 'SNSW-001P8EU':
			// Shelly 1 PM Mini G4
			case 'S4SW-001P8EU':
			// Shelly EM Mini G3
			case 'S3SW-001P8EU':
			// Shelly Pro 2 Cover G2
			case 'SPSH-002PE16EU':
			// Shelly Pro 2 Cover G4
			case 'SPSH-102PE16EU':
			// Shelly 1 PM G3
			case 'S3SW-001P16EU':
			// Shelly Ogemray 30A relay
			case 'S3PB-O3AR000001':
			// Shelly 1 PM G4
			case 'S4SW-001P16EU':
			// Simone 1PM
			case 'S3SW-001P16SP':
			case 'S3SW-002P16EU': //Shelly 2 PM G3
			case 'S3SH-0A2P4EU': // Shelly Shutter
			case 'S4SW-002P16EU': //Shelly 2 PM G4
			case 'S4PL-00114EU': //Shelly Plug C Gen4
			case 'S3PB-0709114901': //Shelly Raex MP46 Curtain Motor
			case 'S3PB-0A15194101': // Yihao YH-002A1 the same as S3XT-0S
				if (device.category === 'roller') {
					if (typeof status[`cover:` + device.channel] === 'undefined') break;

					// const inverted = settings[`cover:` + device.channel].invert_directions
					// might use later to display a cover with inverted directions

					// we should adapt to cover devices state to show a better UX
					const state = status[`cover:` + device.channel]?.state;

					const hasSlat = hasTilt(device) && settings[`cover:` + device.channel]?.slat?.enable;
					const position = status[`cover:` + device.channel].current_pos;
					const slatPosition = status[`cover:` + device.channel]?.slat_pos || 50; //after factory reset this value is undefined
					if (status[`cover:` + device.channel].pos_control && position !== null) {
						interface_data.push({
							name: 'position',
							icon: position > 0 ? 'fa fa-blinds-raised' : 'fa fa-blinds',
							value: position + ' %',
						});
					}
					if (hasSlat) {
						//tilt function
						interface_data.push({
							name: 'slat',
							icon: slatPosition > 10 ? 'fa fa-blinds-open' : 'fa fa-blinds',
							value: slatPosition + ' %',
						});
					}
					if (isYihao(device.type)) {
						break;
					}
					interface_data.push({
						name: 'power',
						icon: 'fas fa-bolt',
						value:
							state !== 'stopped'
								? convert(status?.['cover:' + device.channel]?.apower, 'W')
								: 0 + ' W',
					});
				} else {
					interface_data = [
						{
							name: 'power',
							icon: 'fas fa-bolt',
							value: status?.['switch:' + device.channel]?.output
								? convert(status?.['switch:' + device.channel]?.apower, 'W')
								: 0 + ' W',
						},
					];
				}

				if (typeof status?.[structure]?.voltage !== 'undefined') {
					interface_data.push({
						name: 'voltage',
						icon: 'fas fa-circle-bolt',
						value: (status[structure].voltage ?? 0) + ' V',
					});
				}

				if (typeof status?.[structure]?.freq !== 'undefined') {
					interface_data.push({
						name: 'frequency',
						icon: 'fas fa-waveform',
						value: (status[structure].freq ?? 0) + ' Hz',
						advLabel: true,
					});
				}

				if (typeof status?.[structure]?.aenergy?.total !== 'undefined') {
					interface_data.push({
						name: 'total',
						icon: 'fas fa-bolt',
						value: convert(status[structure].aenergy.total, 'Wh'),
						advLabel: true,
					});
				}

				if (typeof status?.[structure]?.current !== 'undefined') {
					interface_data.push({
						name: 'current',
						icon: 'fas fa-plug-circle-bolt',
						value: convert(status[structure].current, 'A'),
						advLabel: true,
					});
				}

				if (typeof status?.[structure]?.pf !== 'undefined') {
					interface_data.push({
						name: 'pf',
						icon: 'fas fa-wave-pulse',
						value: (status[structure].pf ?? 0) + ' PF',
						advLabel: true,
					});
				}

				if (device.category === 'ext_temp_sensor') {
					interface_data = [
						// 	{
						// 		icon: faHumidity,
						// 		value: mathEpsilon(status[`humidity:${device.channel}`].rh) + ' %'
						// 	},
						{
							name: 'temperature',
							icon: 'fas thermometer-three-quarters',
							value: `${mathEpsilon(
								typeof status[`temperature:10${device.channel - 1}`].temperature !==
									'undefined'
									? status[`temperature:10${device.channel - 1}`].temperature[
											settings?.ht_ui?.temperature_unit == 'F' ? ' tF' : ' tC'
										]
									: status[`temperature:10${device.channel - 1}`][
											settings?.ht_ui?.temperature_unit == 'F' ? ' tF' : ' tC'
										],
							)}${settings?.ht_ui?.temperature_unit == 'F' ? ' °F' : ' °C'}`,
							main: true,
							big_val: true,
						},
					];
				}

				if (typeof status?.['illuminance:0'] !== 'undefined') {
					interface_data.push({
						name: 'illuminance',
						icon: 'fa fa-sun',
						value: extractLuxValueWallDisplay(status, settings),
					});
				}

				break;
			//#endregion PM devices

			//#region Non-PM devices
			// Shelly PRO 1
			case 'SPSW-001XE16EU':
			case 'SPSW-101XE16EU':
			case 'SPSW-201XE16EU':
			case 'SPSW-201XE15UL':
			// Shelly PRO 2
			case 'SPSW-002XE16EU':
			case 'SPSW-102XE16EU':
			case 'SPSW-202XE16EU':
			case 'SPSW-202XE12UL':
			// Shelly PRO 3
			case 'SPSW-003XE16EU':
			// Shelly Plus 1
			case 'SNSW-001X16EU':
			// Shelly Plus 2
			case 'SNSW-002X16EU':
			// Shelly 1 G3
			case 'S3SW-001X16EU':
			// Shelly 1 Mini G4
			case 'S4SW-001X8EU':
				// ignore for global temp unit
				if (device.category === 'ext_temp_sensor') {
					interface_data = [
						// 	{
						// 		icon: faHumidity,
						// 		value: mathEpsilon(status[`humidity:${device.channel}`].rh) + ' %'
						// 	},
						{
							name: 'temperature',
							icon: 'fas thermometer-three-quarters',
							value: `${mathEpsilon(
								typeof status[`temperature:10${device.channel - 1}`].temperature !==
									'undefined'
									? status[`temperature:10${device.channel - 1}`].temperature[
											settings?.ht_ui?.temperature_unit == 'F' ? ' tF' : ' tC'
										]
									: status[`temperature:10${device.channel - 1}`][
											settings?.ht_ui?.temperature_unit == 'F' ? ' tF' : ' tC'
										],
							)}${settings?.ht_ui?.temperature_unit == 'F' ? ' °F' : ' °C'}`,
							main: true,
							big_val: true,
						},
					];
				}
				break;
			case 'S3GW-0D4G0EU': //Shelly g3 4g gateway
				//devicepower:0: {id: 0, battery: {V: 4.38, percent: 100}, external: {present: true}}
				if (status?.['devicepower:0']?.external?.present) {
					interface_data.push({
						name: 'battery',
						icon: 'fa fa-plug',
						value: _format('ext_power_hnt'),
					});
				} else {
					interface_data.push({
						name: 'battery',
						icon: 'fa-duotone fa-light fa-battery-full',
						value: `${status?.[`devicepower:${device?.channel}`]?.battery.percent || 0} % `,
						iconStyle: getBatteryIconStyle(
							status?.[`devicepower:${device?.channel}`]?.battery?.percent,
						),
					});
				}
				break;
			//#endregion Non-PM devices

			// Shelly Plus Wall Dimmer
			case 'SNDM-0013US':
				interface_data = [
					{
						name: 'brightness',
						icon: 'fa-light fa-sun-bright',
						value: status[`light:${device.channel}`].output
							? status[`light:${device.channel}`].brightness + ' %'
							: 0 + ' %',
					},
				];
				break;
			// Shelly Plus H&T
			case 'SNSN-0013A':
			case 'S3SN-0U12A':
			case 'S3SN-1U12A': //h&t g3 ffs
				valueFormat = settings?.ht_ui?.temperature_unit == 'F' ? 0 : 1;
				interface_data = [
					{
						name: 'humidity',
						icon: 'fa fa-humidity',
						value: mathEpsilon(status[`humidity:${device.channel}`]?.rh, 1) + ' %',
					},
					{
						name: 'temperature',
						translation: capFirstChar(
							_format('title_chart_temperature', { default: 'temperature' }),
						),
						icon: 'fa fa-thermometer-three-quarters',
						value: `${mathEpsilon(
							typeof status[`temperature:${device.channel}`] !== 'undefined' &&
								status[`temperature:${device.channel}`]?.[
									settings?.ht_ui?.temperature_unit == 'F' ? 'tF' : 'tC'
								],
							1,
						).toFixed(
							valueFormat,
						)}${settings?.ht_ui?.temperature_unit == 'F' ? ' °F' : ' °C'}`,
						main: true,
						big_val: true,
					},
				];

				if (status[`devicepower:${device.channel}`]?.external.present) {
					interface_data.push({
						name: 'battery',
						icon: 'fa fa-plug',
						value: _format('ext_power_hnt'),
					});
				} else {
					interface_data.push({
						name: 'battery',
						icon: 'fa-duotone fa-light fa-battery-full',
						value: `${status?.[`devicepower:${device?.channel}`]?.battery.percent || 0} % `,
						iconStyle: getBatteryIconStyle(
							status?.[`devicepower:${device?.channel}`]?.battery?.percent,
						),
					});
				}
				break;

			// Shelly Plus Smoke
			case 'SNSN-0031Z':
			case 'S4SN-1031Z':
			case 'S4SN-0031Z':
			case 'SMSN-0031ZL': //Smoke LTE
				const isSleeping =
					typeof status?.['_sleeping'] !== 'undefined' && !!status?.['_sleeping'];
				const hasAlarm = status?.[`smoke:${device.channel}`]?.alarm; //Smoke is detected
				const isMuted = status?.[`smoke:${device.channel}`]?.mute; //Smoke is detected but current alarm is muted(from physical button or form applications)
				const testAlarm = status?.sys?.wakeup_reason?.cause === 'alarm_test'; //triggered from physical button

				const gasSensorStatus = {
					name: 'smoke',
					main: true,
					icon:
						hasAlarm && !isMuted
							? 'text-danger fa-solid fa-fire-smoke'
							: hasAlarm && isMuted
								? 'text-danger fa fa-volume-slash'
								: 'fak fa-light-fire-smoke-slash-tr',
					value:
						hasAlarm && !isMuted //alarm awakening
							? _format('smoke_detected', { default: 'Smoke' })
							: hasAlarm && isMuted //alarm awakening but muted
								? _format('text_muted', { default: 'muted' })
								: testAlarm && !isSleeping //test alarm awakening
									? _format('button_self_test', { default: 'Self test' })
									: !status.hasOwnProperty('_sleeping') || isSleeping
										? _format('status_no', { default: 'No' })
										: _format('setup', { default: 'Setup' }),
				};
				const gasbatteryStatus = {
					name: 'battery',
					icon:
						status?.[`devicepower:${device?.channel}`]?.battery?.percent <= 10
							? 'fa fa-battery-empty'
							: status?.[`devicepower:${device?.channel}`]?.battery?.percent <= 25
								? 'fa-duotone fa-light fa-battery-quarter'
								: 'fa-duotone fa-light fa-battery-full',
					value: `${
						status?.[`devicepower:${device?.channel}`]?.battery.percent
							? status?.[`devicepower:${device?.channel}`]?.battery.percent + ' %'
							: 'N/A'
					} `,
					...(status?.[`devicepower:${device?.channel}`]?.battery?.percent <= 10
						? { iconColor: '--color-danger' }
						: {
								iconStyle: getBatteryIconStyle(
									status?.[`devicepower:${device?.channel}`]?.battery?.percent,
								),
							}),
				}; //after factory reset battery percentage stays null until client hit self test
				interface_data = [gasSensorStatus, gasbatteryStatus];

				break;
			// Shelly Alarm Bridge (S3SN-0082A)
			case 'S3SN-0082A': {
				const hasAlarm = status?.['smoke:0']?.alarm;
				const isMuted = status?.['smoke:0']?.mute;

				const smokeStatus = {
					name: 'smoke',
					main: true,
					icon:
						hasAlarm && !isMuted
							? 'text-danger fa-solid fa-fire-smoke'
							: hasAlarm && isMuted
								? 'text-danger fa fa-volume-slash'
								: 'fak fa-light-fire-smoke-slash-tr',
					value:
						hasAlarm && !isMuted
							? _format('button_yes', { default: 'Yes' })
							: hasAlarm && isMuted
								? _format('button_yes', { default: 'Yes' }) +
									', ' +
									_format('text_muted', { default: 'mute' })
								: _format('status_no', { default: 'No' }),
				};
				const batteryStatus = {
					name: 'battery',
					icon:
						status?.['devicepower:0']?.battery?.percent <= 10
							? 'fa fa-battery-empty'
							: status?.['devicepower:0']?.battery?.percent <= 25
								? 'fa-duotone fa-light fa-battery-quarter'
								: 'fa-duotone fa-light fa-battery-full',
					value: `${
						status?.['devicepower:0']?.battery?.percent != null
							? status?.['devicepower:0']?.battery?.percent + ' %'
							: 'N/A'
					} `,
					...(status?.['devicepower:0']?.battery?.percent <= 10
						? { iconColor: '--color-danger' }
						: {
								iconStyle: getBatteryIconStyle(
									status?.['devicepower:0']?.battery?.percent,
								),
							}),
				};
				interface_data = [smokeStatus, batteryStatus];
				break;
			}
			case 'SPEM-002CEBEU50':
			case 'S3EM-002CXCEU': // Plus EM Gen3
			case 'S4EM-002CXCEU': // EM Gen4
			case 'S4EM-001PXCEU16': // EM Mini GEN 4
			case 'S4SN-0U61XEU': // Shelly Presence
			case 'S4EM-001CXCEU63': // EM-63 Gen4
			case 'SPEM-003CEBEU':
			case 'SPEM-003CEBEU400':
			case 'SPEM-003REBEU2000':
			case 'SPEM-003CEBEU63':
			case 'EFPEM-003CEBEU120':
			case 'S3EM-003CXCEU63':
			case 'S4EM-003RXCEU63':
			case 'S4PB-06A7000101': // Shelly Daisy EM
				if (
					typeof status !== 'undefined' &&
					typeof device !== 'undefined' &&
					typeof settings !== 'undefined'
				) {
					//added this extra check because it breaks on profile change
					interface_data = emeter_status_configurator(status, device, settings);
				}
				break;
			case 'SPCB-01VENEU':
			case 'SPCB-02VENEU':
			case 'SPCB-03VENEU':
			case 'SPCB-04VENEU': //shelly pro circuit breakers
				if (typeof status !== 'undefined' && status?.['voltmeter:0']?.voltage) {
					interface_data = [
						{
							name: 'voltage',
							icon: 'fas fa-circle-bolt',
							value: `${status?.['voltmeter:0']?.voltage} V`,
						},
					];
				}
				for (let i = 1; i <= 5; i++) {
					if (status?.[`voltmeter:${i}`]) {
						interface_data.push({
							name: `voltage_${i}`,
							icon: 'fas fa-circle-bolt',
							value: `${status?.[`voltmeter:${i}`]?.voltage} V`,
						});
					}
				}
				if (typeof status?.['cb:0']?.safety !== 'undefined' && status?.['cb:0']?.safety) {
					interface_data.push({
						name: 'safety',
						icon: 'fas fa-lock',
						value: _format('label_safety_switch_activated', {
							default: 'Safety switch activated',
						}),
					});
				}
				break;

			case 'SAWD-0A1XX10EU1':
			case 'SAWD-1A1XX10EU1':
			case 'SAWD-2A1XX10EU1':
			case 'SAWD-3A2XE10EU2':
			case 'SAWD-3A1XE10EU2':
			case 'SAWD-4A1XE10US0':
			case 'SAWD-5A1XX10EU0':
			case 'SAWD-6A1XX10EU0':
			case 'SAWD-6A0XX0EU0':
				const isThermostatAsDevice =
					device.id.includes('_101') && device.category === 'thermostat';
				const isMediaPlayerAsDevice =
					device.id.includes('_100') && device.category === 'media_player';
				const isPlaying = status?.media?.playback?.enable;
				const heading = isPlaying ? status?.media?.playback?.media_meta?.title : undefined;
				const isActuator = isActuatorHelper(settings, device);
				const currentTemperature =
					typeof status?.['thermostat:0'] !== 'undefined' &&
					status?.['thermostat:0']?.current_C &&
					status?.['thermostat:0']?.current_C > -273; //check if current temperature is available because -273 is the default value for error

				if (isThermostatAsDevice) {
					if (
						status?.['temperature:0']?.tC > -275 &&
						status?.['temperature:0']?.errors?.length === 0
					) {
						//if temperature is available
						interface_data.push({
							main: isActuator ? isActuator : undefined, //visualizing temperature if not actuator
							name: 'temperature',
							icon: 'fa fa-thermometer-three-quarters',
							value: extractTemperatureValueWallDisplay(status),
						});
					} else if (
						status?.['temperature:0']?.errors?.length > 0 &&
						status?.['temperature:0']?.errors[0] !== 'Sensor driver missing from firmware'
					) {
						interface_data.push({
							main: isActuator ? isActuator : undefined, //visualizing temperature if not actuator
							name: 'temperature',
							icon: 'fa fa-thermometer-three-quarters',
							iconColor: '--color-danger',
							value: 'ERR',
						});
					}
				} else if (isMediaPlayerAsDevice) {
					if (isPlaying && heading && !get(location).includes('dash')) {
						//using location to hide label from media player while used as widget on dash
						interface_data.push({
							name: 'playback',
							icon: 'fa fa-music',
							value: heading,
							push_to: `/ device / ${device.id} `,
							setting_to_open: 'media_player',
						});
					}
				} else {
					// set default interface data for wall display with lux value which has no changes with device firmware
					interface_data = [
						{
							name: 'illuminance',
							icon: 'fa fa-sun',
							value: extractLuxValueWallDisplay(status, settings),
						},
					];

					/*
					 * If the value is below -273 or there is an error different than 'Sensor driver missing from firmware',
					 * we will display an error message, otherwise the temperature status is hidden from the interface.
					 */
					if (currentTemperature) {
						interface_data.push({
							name: 'thermostat_temperature',
							icon: 'fa fa-thermometer-three-quarters',
							value: extractTemperatureValueWallDisplay(status, true),
						});
					}
					if (status?.['temperature:0']?.tC > -275) {
						//if temperature reading is available
						interface_data.push({
							main: isActuator ? isActuator : undefined, //  visualizing temperature if not actuator
							name: 'temperature',
							icon: 'fa fa-thermometer-three-quarters',
							value: extractTemperatureValueWallDisplay(status),
						});
					} else if (
						status?.['temperature:0']?.errors?.length > 0 &&
						status?.['temperature:0']?.errors[0] !== 'Sensor driver missing from firmware'
					) {
						//if there is an error different than 'Sensor driver missing from firmware'
						interface_data.push({
							main: isActuator ? isActuator : undefined, //visualizing temperature if not actuator
							name: 'temperature',
							icon: 'fa fa-thermometer-three-quarters',
							iconColor: '--color-danger',
							value: 'ERR',
						});
					}

					if (status?.['humidity:0']?.rh > 0) {
						//if humidity is available
						interface_data.push({
							name: 'humidity',
							icon: 'fa fa-humidity',
							value: extractHumidityValueWallDisplay(status),
						});
					} else if (
						status?.['humidity:0']?.errors?.length > 0 &&
						status?.['humidity:0']?.errors[0] !== 'Sensor driver missing from firmware'
					) {
						//if there is an error different than 'Sensor driver missing from firmware'
						interface_data.push({
							name: 'humidity',
							icon: 'fa fa-humiditys',
							iconColor: '--color-danger',
							value: 'ERR',
						});
					}

					if (typeof status['thermostat:0']?.output !== 'undefined') {
						interface_data.push({
							//input label isn't that important so let's put it at the end
							name: 'power',
							icon: status['thermostat:0']?.output
								? 'fa fa-toggle-on'
								: 'fa fa-toggle-off',
							value: _format(
								`${status['thermostat:0']?.output ? 'label_output_on' : 'label_output_off'}`,
							),
						});
					}
					if (isPlaying) {
						interface_data.push({
							name: 'playback',
							icon: 'fa fa-music',
							value: heading,
							push_to: `/ device / ${device.id} `,
							setting_to_open: 'media_player', //unfortunately this navigation isn't supported
						});
					}
				}
				break;
			case 'SNDC-0D4P10WW': //plusRGBW
				if (typeof status[structure] !== 'undefined') {
					if (typeof status[structure]?.rgb?.[0] !== 'undefined') {
						interface_data = [
							{
								name: 'color',
								icon: 'fa-duotone fa-light fa-circle',
								iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${status[structure].rgb[0]},${status[structure].rgb[1]},${status[structure].rgb[2]}); --fa-secondary-opacity: 1`,
							},
						];
					}
					if (typeof status?.[structure]?.brightness !== 'undefined') {
						interface_data = [
							{
								name: 'brightness',
								icon: 'fa-light fa-sun-bright',
								value:
									(status[structure]?.output ? status[structure].brightness : 0) +
									' %',
							},
						];
					}
					if (typeof status?.[structure]?.apower !== 'undefined') {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-bolt',
							value: convert(status[structure].apower, 'W'),
						});
					}

					if (typeof status?.[structure]?.calibration !== 'undefined') {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-ruler',
							value: status[structure].calibration.progress,
						});
					}

					if (typeof status?.[structure]?.voltage !== 'undefined') {
						interface_data.push({
							name: 'voltage',
							icon: 'fas fa-circle-bolt',
							value: (status[structure].voltage ?? 0) + ' V',
						});
					}
				}
				break;
			//Plus 010V
			case 'SNDM-00100WW':
			case 'SPDM-001PE01EU':
			case 'SPDM-002PE01EU':
			case 'S3DM-0010WW':
			case 'S3DM-0A101WWL': // Shelly Dimmer G3
			case 'S3DM-0A1WW': // Shelly DALI Dimmer G3
			case 'S4DM-0A101WWL': // Shelly Dimmer G4
			case 'S4DM-0A102US': // Shelly Dimmer G4 US
			case 'S4DM-0010WW': // Shelly Dimmer 0/1-10V PM G4
			case 'SPCC-001PE10EU': //ShellyPro0110PM
				if (typeof status?.[structure] !== 'undefined') {
					if (!status?.[structure]?.flags?.includes('uncalibrated')) {
						interface_data = [
							{
								name: 'brightness',
								icon: 'fa-light fa-sun-bright',
								value:
									(status?.[structure]?.output
										? status?.[structure]?.brightness?.toFixed(0) //because device's status returns float. We need to visualize it as integer
										: 0) + ' %',
							},
						];
					}
					if (typeof status?.[structure]?.apower !== 'undefined') {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-bolt',
							value: convert(status[structure].apower, 'W'),
						});
					}
					if (typeof status?.[structure]?.calibration !== 'undefined') {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-ruler',
							value: status[structure].calibration.progress,
						});
					}
					if (typeof status?.[structure]?.voltage !== 'undefined') {
						interface_data.push({
							name: 'voltage',
							icon: 'fas fa-circle-bolt',
							value: (status[structure].voltage ?? 0) + ' V',
						});
					}
				}
				break;
			case 'SNPM-001PCEU16':
			case 'S3PM-001PCEU16':
			case 'S3PL-30116EU': // Shelly Plug PM Gen3
			case 'S4PL-00116EU': // Shelly Plug PM Gen4
				if (isNoRelayPlug(device.type)) {
					if (!status || !structure || status[structure] === undefined) {
						break;
					}
					interface_data = [
						{
							name: 'power',
							main: true,
							icon: 'fas fa-bolt',
							value:
								status[structure]?.apower !== null
									? convert(status[structure].apower, 'W')
									: Object.keys(status[structure] || {}).filter(
												(k) => k.indexOf('apower') !== -1,
										  ).length > 0
										? convert(
												Object.keys(status[structure])
													.filter((k) => k.indexOf('apower') !== -1)
													.map((k) => status[structure][k])
													.reduce((a, b) => a + b, 0),
												'W',
											)
										: '0 W',
						},
					];
				} else {
					interface_data = [
						{
							name: 'power',
							main: true,
							icon: 'fas fa-bolt',
							value:
								status[structure]?.apower !== 'undefined'
									? convert(status[structure].apower, 'W')
									: convert(
											Object.keys(status[structure])
												.filter((k) => k.indexOf('apower') !== -1)
												.map((k) => status[structure][k])
												.reduce((a, b) => a + b),
											'W',
										),
						},
					];
				}

				if (typeof status?.[structure]?.voltage !== 'undefined') {
					interface_data.push({
						name: 'voltage',
						icon: 'fas fa-circle-bolt',
						value: (status[structure].voltage ?? 0) + ' V',
						advLabel: true,
					});
				}

				if (typeof status?.[structure]?.freq !== 'undefined') {
					interface_data.push({
						name: 'frequency',
						icon: 'fas fa-waveform',
						value: (status[structure].freq ?? 0) + ' Hz',
						advLabel: true,
					});
				}

				if (typeof status?.[structure]?.aenergy?.total !== 'undefined') {
					interface_data.push({
						name: 'total',
						icon: 'fas fa-bolt',
						value: convert(status[structure].aenergy.total, 'Wh'),
						advLabel: true,
					});
				}

				if (typeof status?.[structure]?.current !== 'undefined') {
					interface_data.push({
						name: 'current',
						icon: 'fas fa-plug-circle-bolt',
						value: convert(status[structure].current, 'A'),
						advLabel: true,
					});
				}

				if (typeof status?.[structure]?.pf !== 'undefined') {
					interface_data.push({
						name: 'pf',
						icon: 'fas fa-wave-pulse',
						value: (status[structure].pf ?? 0) + ' PF',
						advLabel: true,
					});
				}

				if (device.category === 'ext_temp_sensor') {
					interface_data = [
						// 	{
						// 		icon: faHumidity,
						// 		value: mathEpsilon(status[`humidity:${device.channel}`].rh) + ' %'
						// 	},
						{
							name: 'temperature',
							icon: 'fas thermometer-three-quarters',
							value: `${mathEpsilon(
								typeof status[`temperature:10${device.channel - 1}`].temperature !==
									'undefined'
									? status[`temperature:10${device.channel - 1}`].temperature[
											settings?.ht_ui?.temperature_unit == 'F' ? ' tF' : ' tC'
										]
									: status[`temperature:10${device.channel - 1}`][
											settings?.ht_ui?.temperature_unit == 'F' ? ' tF' : ' tC'
										],
							)}${settings?.ht_ui?.temperature_unit == 'F' ? ' °F' : ' °C'}`,
							main: true,
							big_val: true,
						},
					];
				}

				if (typeof status?.['illuminance:0'] !== 'undefined') {
					interface_data.push({
						name: 'illuminance',
						icon: 'fa fa-sun',
						value: extractLuxValueWallDisplay(status, settings),
					});
				}

				break;

			//#endregion Gen 2

			//#region gen 3
			case 'S3BL-D010009AEU': //vtac duo
			case 'S3PB-04F7045601': //Hilux
			case 'S3MX-0A':
			case 'S3XT-0S':
				if (typeof status?.[structure] !== 'undefined') {
					const componentType = structure?.split(':')?.[0];

					if (componentType === 'cct') {
						let rgb = kelvin_to_rgb(status?.[structure]?.ct);
						const ison = status?.[structure]?.output;
						interface_data = [
							{
								name: 'color_temp',
								icon: 'fa-duotone fa-light fa-circle',
								iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${rgb[0]},${rgb[1]},${rgb[2]}); --fa-secondary-opacity: 1`,
								value:
									typeof status?.[structure]?.ct === 'number'
										? `${status[structure].ct} K`
										: '',
							},
							{
								name: 'brightness',
								icon: 'fa-light fa-sun-bright',
								value: ison ? status?.[structure]?.brightness + ' %' : 0 + ' %',
							},
						];
						if (typeof status?.[structure]?.apower !== 'undefined') {
							interface_data.splice(1, 0, {
								name: 'power',
								icon: 'fas fa-bolt',
								value: ison ? convert(status?.[structure]?.apower, 'W') : 0 + ' W',
							});
						}
					} else if (componentType === 'rgb' || componentType === 'rgbw') {
						const rgb = status?.[structure]?.rgb;
						const brightness = status?.[structure]?.brightness;
						const ison = status?.[structure]?.output;
						if (rgb && rgb?.[0] !== undefined) {
							interface_data.push({
								name: 'color',
								icon: 'fa-duotone fa-light fa-circle',
								iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${rgb[0]},${rgb[1]},${rgb[2]}); --fa-secondary-opacity: 1`,
							});
						}
						if (typeof brightness !== 'undefined') {
							interface_data.push({
								name: 'brightness',
								icon: 'fa-light fa-sun-bright',
								value: ison ? brightness + ' %' : 0 + ' %',
							});
						}
					} else if (componentType === 'cover') {
						const coverStatus = status[structure];
						const position = coverStatus?.current_pos;

						interface_data.push({
							name: 'state',
							icon: 'fa fa-blinds',
							value: coverStatus?.state,
							main: true,
						});

						if (coverStatus?.pos_control && typeof position === 'number') {
							interface_data.push({
								name: 'position',
								icon: position > 0 ? 'fa fa-blinds-raised' : 'fa fa-blinds',
								value: position + ' %',
							});
						}
					}
				}
				break;
			case 'S4PB-00CU000002': //Cury
				const slots = status?.[structure]?.slots;
				const colors = settings?.[structure]?.ambient?.color;
				const isAmbientEnabled = settings?.[structure]?.ambient?.enable;
				const vialFault = getVialFaultError(status?.[structure]?.slots);
				const curyMode = status?.[structure]?.mode;
				const curyAwayMode = status?.[structure]?.away_mode;

				// Show current mode if set
				if (curyMode && !curyAwayMode) {
					interface_data.push({
						name: 'mode',
						icon: 'fas fa-building',
						value: _format(`label_${curyMode}`, { default: curyMode.replace('_', ' ') }),
						iconColor: '--color-info',
					});
				}

				// Show away mode if active
				if (curyAwayMode) {
					interface_data.push({
						name: 'away_mode',
						icon: 'fas fa-suitcase',
						value: _format('away_mode_active', { default: 'Away Mode' }),
						iconColor: '--color-warning',
					});
				}

				if (typeof slots !== 'undefined') {
					// Process slots in order: left first, then right to maintain consistent UI layout
					const orderedSlots = ['left', 'right'].filter((slot) => slots[slot] !== undefined);

					orderedSlots.forEach((slot) => {
						// Extract slot-specific data
						const slotLabel = slot === 'left' ? 'L:' : 'R:';
						const slotStatus = status[structure].slots?.[slot];
						const isSlotAvailable = slotStatus !== null && !vialFault[slot];
						const vialLevel = slotStatus?.vial?.level;
						const iconColor = vialLevel > 0 ? '--color-info' : '--color-danger';
						const boostInfo = slotStatus?.boost;

						if (isSlotAvailable) {
							interface_data.push({
								name: `vial ${slot}`,
								icon: 'fas fa-spray-can',
								iconColor,
								value: `${slotLabel} ${getLimitedValue(vialLevel, 100)} %`,
							});
						}

						if (boostInfo?.started_at && boostInfo?.duration) {
							interface_data.push({
								name: 'timer',
								icon: 'fa fa-timer',
								value: `${boostInfo.duration}s`,
								iconColor: '--color-primary',
								push_to: `/ device / ${device.id} `,
								setting_to_open: 'timer',
							});
						}

						if (!isSlotAvailable) {
							interface_data.push({
								name: `vial ${slot}`,
								value: vialFault[slot]
									? `${slotLabel} ${translateVialFaultError(vialFault[slot])?.short}`
									: `${slotLabel} ${_format('label_no_vial_detected', { default: 'No Vial Detected' })}`,
								icon: vialFault[slot]
									? 'fal fa-exclamation-triangle'
									: 'fal fa-circle-xmark',
								iconColor: vialFault[slot] ? '--color-warning' : undefined,
							});
						}
					});
				}
				if (isAmbientEnabled) {
					interface_data.push({
						name: 'ambient',
						icon: 'fa-duotone fa-light fa-circle',
						iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${colors[0]},${colors[1]},${colors[2]}); --fa-secondary-opacity: 1`,
					});
				}
				break;
			case 'S3BL-C010007AEU':
				const _structure = get_gen_2_component_channel_structure(device);
				if (typeof status?.[_structure]?.output !== 'undefined') {
					interface_data = [
						{
							name: 'brightness',
							icon: 'fa-light fa-sun-bright',
							value: status?.[_structure]?.output
								? status?.[_structure]?.brightness + ' %'
								: 0 + ' %',
						},
					];

					if (
						typeof status?.[_structure]?.rgb !== 'undefined' &&
						status?.[_structure]?.mode === 'rgb'
					) {
						let rgb = status?.[_structure].rgb;
						interface_data.push({
							name: 'color',
							icon: 'fa-duotone fa-light fa-circle',
							iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${rgb[0]},${rgb[1]},${rgb[2]}); --fa-secondary-opacity: 1`,
						});
					}
					if (
						typeof status?.[_structure]?.ct !== 'undefined' &&
						status?.[_structure]?.mode === 'cct'
					) {
						let rgb = kelvin_to_rgb(status?.[_structure].ct);
						interface_data.push({
							name: 'color_temp',
							icon: 'fa-duotone fa-light fa-circle',
							iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${rgb[0]},${rgb[1]},${rgb[2]}); --fa-secondary-opacity: 1`,
							value: `${status[_structure].ct} K`,
						});
					}
					if (typeof status?.[_structure]?.apower !== 'undefined') {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-bolt',
							value: convert(status[_structure].apower, 'W'),
						});
					}
				}
				break;

			case 'SPDC-0D5PE16EU':
				if (typeof status[structure] !== 'undefined') {
					if (typeof status[structure]?.rgb?.[0] !== 'undefined') {
						interface_data = [
							{
								name: 'color',
								icon: 'fa-duotone fa-light fa-circle',
								iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${status[structure].rgb[0]},${status[structure].rgb[1]},${status[structure].rgb[2]}); --fa-secondary-opacity: 1`,
							},
						];
					}
					if (typeof status[structure]?.brightness !== 'undefined') {
						interface_data.push({
							name: 'brightness',
							icon: 'fa-light fa-sun-bright',
							value: (status[structure].output ? status[structure].brightness : 0) + ' %',
						});
					}
					if (typeof status?.[structure]?.apower !== 'undefined') {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-bolt',
							value: convert(status[structure].apower, 'W'),
						});
					}

					if (typeof status?.[structure]?.calibration !== 'undefined') {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-ruler',
							value: status[structure].calibration.progress,
						});
					}

					if (typeof status?.[structure]?.voltage !== 'undefined') {
						interface_data.push({
							name: 'voltage',
							icon: 'fas fa-circle-bolt',
							value: (status[structure].voltage ?? 0) + ' V',
						});
					}
				}
				break;
			case 'S4SN-0071A':
			case 'S4SN-0071Z':
				const rainMode = settings?.['flood:0']?.alarm_mode === 'rain'; //with this setting we will change label to normal while flood is detected.
				const isAlarmActive = status?.[`flood:0`]?.alarm;
				const isAlarmMuted = status?.[`flood:0`]?.mute;

				{
					const name = rainMode ? 'rain' : 'flood';
					let icon = 'fas fa-droplet-slash';
					let iconColor = '';
					let value = _format('status_no', { default: 'No' });

					if (isAlarmActive) {
						if (isAlarmMuted) {
							icon = 'text-danger fa fa-volume-slash';
							value = _format('text_muted', { default: 'muted' });
						} else if (rainMode) {
							icon = 'fa fa-raindrops';
							value = _format('prop_rain', { default: 'Rain' });
						} else {
							icon = 'text-danger fa fa-house-flood-water';
							iconColor = '--color-danger';
							value = _format('prop_flood', { default: 'Flood' });
						}
					}

					interface_data.push({
						name,
						icon,
						iconColor,
						value,
						main: true,
					});
				}

				if (typeof status?.[`devicepower:0`]?.battery !== 'undefined') {
					let batteryPercent = status?.[`devicepower:0`]?.battery?.percent;
					if (batteryPercent === null) {
						batteryPercent = 'N/A';
					}
					const batteryStatus = {
						name: 'battery',
						icon: getBatteryIcon(batteryPercent),
						value: batteryPercent + ' %',
						iconStyle: getBatteryIconStyle(batteryPercent),
					};
					interface_data.push(batteryStatus);
				}
				break;
			case 'S3SN-0U53X': //The Pill by Shelly
				return getPillDeviceStatus(device);
				break;
			case 'S3SW-0A1X1EUL':
			case 'S4SW-0A1X1EUL':
			case 'S3SW-0A1X5US':
			case 'S3SW-1A1X5US':
				interface_data = buildInputInterfaceData(device, status);
				break;
			case 'S4PB-0916176901':
				interface_data = buildS4Pb0916176901StatusLabelList(status, settings);
				break;
			//#region Gen GBLE
			case 'SBBT-002C':
			case 'SBBT-102C':
			case 'SBBT-102CEU':
			case 'SBBT-004C':
			case 'SBBT-004CUS':
			case 'SBBT-004CEU':
			case 'SBBT-104CUS':
			case 'SBBT-104CEU':
				interface_data = [];
				if (typeof status?.[`devicepower:0`]?.battery !== 'undefined') {
					let batteryPercent = status?.[`devicepower:0`]?.battery?.percent;
					const bleButtonBatteryStatus = {
						name: 'battery',
						icon: getBatteryIcon(batteryPercent),
						value: `${
							status?.[`devicepower:0`]?.battery.percent
								? status?.[`devicepower:0`]?.battery.percent + ' %'
								: 'N/A'
						} `,
						iconStyle: getBatteryIconStyle(status?.[`devicepower:0`]?.battery?.percent),
					};

					interface_data.push(bleButtonBatteryStatus);
				}
				break;
			case 'SBRC-005B':
			case 'SBRK-002C':
				interface_data = [];
				if (typeof status?.[`devicepower:0`]?.battery !== 'undefined') {
					let batteryPercent = status?.[`devicepower:0`]?.battery?.percent;
					const bleButtonBatteryStatus = {
						name: 'battery',
						icon: getBatteryIcon(batteryPercent),
						value: `${
							status?.[`devicepower:0`]?.battery.percent
								? status?.[`devicepower:0`]?.battery.percent + ' %'
								: 'N/A'
						} `,
						iconStyle: getBatteryIconStyle(status?.[`devicepower:0`]?.battery?.percent),
					};

					interface_data.push(bleButtonBatteryStatus);
				}

				if (
					device?.type === 'SBRC-005B' &&
					(typeof status?.['channel:0']?.value !== 'undefined' ||
						status?.['channel:0']?.value !== null)
				) {
					interface_data.push({
						name: 'channel',
						icon: 'fal fa-circle-small',
						value: status?.['channel:0']?.value + 1,
					});
				}

				break;
			case 'SBDI-003E':
				const bleDistanceBatteryStatus = {
					name: 'battery',
					icon:
						status?.[`devicepower:0`]?.battery?.percent <= 10
							? 'fa fa-battery-empty'
							: status?.[`devicepower:0`]?.battery?.percent <= 25
								? 'fa-duotone fa-light fa-battery-quarter'
								: 'fa-duotone fa-light fa-battery-full',
					value: `${
						status?.[`devicepower:0`]?.battery.percent
							? status?.[`devicepower:0`]?.battery.percent + ' %'
							: 'N/A'
					} `,
					...(status?.[`devicepower:0`]?.battery?.percent <= 10
						? { iconColor: '--color-danger' }
						: {
								iconStyle: getBatteryIconStyle(
									status?.[`devicepower:0`]?.battery?.percent,
								),
							}),
				};

				interface_data = [bleDistanceBatteryStatus];

				const distanceUnit = device?.ht_ui?.unit || settings?.ht_ui?.unit || 'mm';

				let distanceIcon;
				let distanceValue;
				if (status['distance:0']?.cvalue > 0) {
					distanceIcon = 'fa-solid fa-ruler-horizontal';
					distanceValue = status['distance:0']?.cvalue;
					distanceValue = convertAndFormatDistance(distanceValue, distanceUnit, device.type);
				} else {
					distanceValue = _format('common_out_of_range', {
						default: 'Out of range',
					});
				}

				interface_data.push({
					name: 'distance',
					translation: capFirstChar(_format('title_chart_distance', { default: 'distance' })),
					icon: distanceIcon,
					value: distanceValue,
					big_val: true,
					main: true,
				});

				interface_data.push({
					name: 'vibration',
					translation: capFirstChar(_format('prop_vibration', { default: 'vibration' })),
					icon: `fa-solid fa-${status['vibration:0']?.value ? 'waveform' : 'dash'}`,
					iconColor: status['vibration:0']?.value ? '--color-primary' : '',
					value: capFirstChar(
						status['vibration:0']?.value
							? _format('prop_vibration', { default: 'vibration' })
							: _format('state_no_vibration', { default: 'no vibration' }),
					),
				});

				if (['liquids', 'solids'].includes(device?.modeType)) {
					const liquidLevel = status['distance:0']?.percent || 0;

					interface_data.push({
						name: 'level',
						translation: capFirstChar(_format('label_level', { default: 'level' })),
						icon: `fa-solid fa-${device?.modeType === 'liquids' ? 'droplet' : 'badge'}-percent`,
						value: `${liquidLevel} %`,
						big_val: true,
						main: true,
					});
				} else if (device?.modeType === 'occupancy') {
					const baselineDistance = Number(device?.installationHeight);
					const triggerDistance = Number(device?.triggerDistance);
					const occupancy = status['distance:0']?.occupancy;

					let occupancyIcon;
					let occupancyValue;
					if (occupancy === 'error' || isNaN(baselineDistance) || isNaN(triggerDistance)) {
						occupancyValue = _format('common_out_of_range', {
							default: 'Out of range',
						});
					} else if (occupancy === 'occupied') {
						occupancyIcon = 'fa-solid fa-garage-car';
						occupancyValue = _format('label_occupied', { default: 'Occupied' });
					} else {
						occupancyIcon = 'fa-solid fa-garage-empty';
						occupancyValue = _format('label_vacant', { default: 'Vacant' });
					}

					interface_data.push({
						name: 'occupancy',
						translation: capFirstChar(
							_format('label_parking_spot', { default: 'Parking Spot' }),
						),
						value: occupancyValue,
						icon: occupancyIcon,
						big_val: true,
						main: true,
					});
				} else if (device?.modeType === 'garage') {
					const hasVibration = Boolean(status['vibration:0']?.value);
					const closedPositionRange = Number(device?.closedPositionRange);
					const closedToleranceRange = Number(device?.closedToleranceRange);
					let threshold = 0;
					if (!isNaN(closedPositionRange) && !isNaN(closedToleranceRange)) {
						threshold = closedPositionRange + closedToleranceRange;
					}
					const garageTypeValue =
						typeof device?.garageType === 'string'
							? device?.garageType
							: device?.garageType?.value;
					distanceValue = status['distance:0']?.cvalue;

					let state = garageStates.get(device?.id);
					if (!state) {
						state = {
							stableDistance: distanceValue, // initialize baseline to first observed
							lastDirection: null,
							confirmCount: 0,
							confirmNeeded: 1, // set to 1 = require 1 confirming reading to finalize; set 0 for immediate baseline update
							lastUpdateTs: null,
						};
						garageStates.set(device?.id, state);
					}

					let position;

					if (hasVibration) {
						// compute delta against the stable baseline
						const delta = distanceValue - (state.stableDistance ?? distanceValue);

						// Movement detected
						if (delta > 0) {
							position = 'opening';
							// if same direction as lastDirection -> increment confirm
							if (state.lastDirection === 'opening') {
								state.confirmCount++;
							} else {
								// new direction observed -> reset confirm
								state.lastDirection = 'opening';
								state.confirmCount = 0;
							}
						} else if (delta < 0) {
							position = 'closing';

							if (state.lastDirection === 'closing') {
								state.confirmCount++;
							} else {
								state.lastDirection = 'closing';
								state.confirmCount = 0;
							}
						} else {
							// If we have lastDirection, reuse it
							if (state.lastDirection) {
								position = state.lastDirection;
							} else {
								position = 'opening'; // fallback only on startup
							}
						}

						if (state.confirmCount >= state.confirmNeeded) {
							state.stableDistance = distanceValue;
							state.confirmCount = 0;
						}
					} else {
						// No vibration → door stationary
						position = distanceValue <= threshold ? 'closed' : 'open';

						state.stableDistance = distanceValue;
						state.lastDirection = null;
						state.confirmCount = 0;
					}

					interface_data.push({
						name: 'garage',
						translation: capFirstChar(
							_format('title_garage_door', { default: 'Garage door' }),
						),
						value: capFirstChar(
							_format(position === 'open' ? 'state_open_copy' : `state_${position}`, {
								default: position,
							}),
						),
						big_val: true,
						main: true,
						icon: getGarageIconStatus(garageTypeValue, position),
					});
				}

				break;
			case 'SBDW-302C': {
				const channel = device.channel;
				const power = status?.['devicepower:0']?.battery;
				const doorStatus = status?.[`window:${channel}`];
				const rotation = status?.[`rot:${channel}`];
				const tilt = status?.[`tilt:${channel}`];

				interface_data = [
					{
						name: 'door',
						icon: doorStatus?.open ? 'text-primary fa fa-door-open' : 'fa fa-door-closed',
						value: _format(doorStatus?.open ? 'state_opened' : 'state_closed'),
						main: true,
					},
				];

				const batteryPercent = power?.percent;
				interface_data.push({
					name: 'battery',
					icon:
						batteryPercent <= 10
							? 'fas fa-battery-empty'
							: batteryPercent <= 25
								? 'fa-duotone fa-light fa-battery-quarter'
								: 'fa-duotone fa-light fa-battery-full',
					value: batteryPercent != null ? `${batteryPercent} %` : 'N/A',
					...(batteryPercent <= 10
						? { iconColor: '--color-danger' }
						: { iconStyle: getBatteryIconStyle(batteryPercent) }),
				});

				if (rotation !== undefined) {
					interface_data.push({
						name: 'tilt',
						icon: 'fas fa-angle',
						value: `${rotation.rot} °`,
					});
				} else if (tilt !== undefined) {
					interface_data.push({
						name: 'tilt',
						icon: 'fas fa-angle',
						value: `${tilt.angle} °`,
					});
				}

				break;
			}
			case 'SBDW-002C':
			case 'SBDW-103C':
			case 'SBDW-203C': {
				/**
				 * 1. Pre-extract common data to avoid repetitive optional chaining
				 */
				const channel = device.channel;
				const luxData = status?.[`lux:${channel}`] || status?.[`illuminance:${channel}`];
				const power = status?.['devicepower:0']?.battery;
				const doorStatus = status?.[`window:${channel}`];
				const rotation = status?.[`rot:${channel}`];
				const tilt = status?.[`tilt:${channel}`];
				/**
				 * 2. Determine Illumination early
				 */
				const illuminationValue = status?.['illuminance:0']?.illumination;
				const illuminationError =
					illuminationValue === '' && status?.['illuminance:0']?.lux === -999;

				/**
				 * 3. Start with the main Door state (Prevents overwriting)
				 */
				interface_data = [
					{
						name: 'door',
						icon: doorStatus?.open ? 'text-primary fa fa-door-open' : 'fa fa-door-closed',
						value: _format(doorStatus?.open ? 'state_opened' : 'state_closed'),
						main: true,
					},
				];

				/**
				 * 4. Handle Lux logic (Prioritizes illuminationValue if it exists)
				 */
				if (!illuminationError && illuminationValue) {
					interface_data.push({
						name: 'lux',
						icon: 'fas ' + getIlluminationIcon(illuminationValue),
						iconColor: 'color-secondary',
						value: get(format)(`option_motion_mode_${illuminationValue}`, {
							default:
								illuminationValue.charAt(0).toUpperCase() + illuminationValue.slice(1),
						}),
					});
				} else if (
					typeof luxData?.lux !== 'undefined' &&
					luxData?.lux !== null &&
					!illuminationError
				) {
					interface_data.push({ name: 'lux', icon: 'fa fa-sun', value: luxData.lux });
				}

				/**
				 * 5. Add Battery (Cleaner conditional styling)
				 */
				const batteryPercent = power?.percent;
				interface_data.push({
					name: 'battery',
					icon:
						batteryPercent <= 10
							? 'fas fa-battery-empty'
							: batteryPercent <= 25
								? 'fa-duotone fa-light fa-battery-quarter'
								: 'fa-duotone fa-light fa-battery-full',
					value: batteryPercent != null ? `${batteryPercent} %` : 'N/A',
					...(batteryPercent <= 10
						? { iconColor: '--color-danger' }
						: { iconStyle: getBatteryIconStyle(batteryPercent) }),
				});

				/**
				 * 6. Add Angle (Rotation or Tilt)
				 */
				if (rotation !== undefined) {
					interface_data.push({
						name: 'tilt',
						icon: 'fas fa-angle',
						value: `${rotation.rot} °`,
					});
				} else if (tilt !== undefined) {
					interface_data.push({
						name: 'tilt',
						icon: 'fas fa-angle',
						value: `${tilt.angle} °`,
					});
				}

				break;
			}
			case 'SBMO-003Z':
			case 'SBMO-103Z':
				if (typeof status['motion:' + device.channel] !== 'undefined') {
					interface_data.push({
						name: 'motion',
						icon: status['motion:' + device.channel].motion
							? 'text-primary fa fa-person-walking'
							: 'fas fa-person',
						value: status['motion:' + device.channel].motion
							? _format('motion', { default: 'motion' })
							: _format('status_no', { default: 'no' }),
						main: true,
					});
				}

				if (status?.['illuminance:' + device.channel]) {
					const illuminationValue = status?.['illuminance:0']?.illumination;
					const illuminationIcon = 'fas ' + getIlluminationIcon(illuminationValue);
					if (illuminationValue) {
						interface_data.push({
							name: 'lux',
							icon: illuminationIcon,
							iconColor: 'color-secondary',
							value: get(format)(`option_motion_mode_${illuminationValue}`, {
								default:
									illuminationValue.charAt(0).toUpperCase() +
									illuminationValue.slice(1),
							}),
						});
					} else {
						interface_data.push({
							name: 'lux',
							icon: 'fa fa-sun',
							value: status?.['illuminance:' + device.channel].lux,
						});
					}
				}

				if (status?.['temperature:' + device.channel]) {
					interface_data.push({
						name: 'temperature',
						icon: 'fa fa-thermometer-three-quarters',
						color:
							status[`temperature:${device.channel}`][
								settings?.ht_ui?.temperature_unit == 'F' ? 'tF' : 'tC'
							] == null ||
							status[`temperature:${device.channel}`][
								settings?.ht_ui?.temperature_unit == 'F' ? 'tF' : 'tC'
							] == -999
								? 'var(--color-danger)'
								: '',
						iconColor:
							status[`temperature:${device.channel}`][
								settings?.ht_ui?.temperature_unit == 'F' ? 'tF' : 'tC'
							] == null ||
							status[`temperature:${device.channel}`][
								settings?.ht_ui?.temperature_unit == 'F' ? 'tF' : 'tC'
							] == -999
								? '--color-danger'
								: '',
						value:
							status[`temperature:${device.channel}`][
								settings?.ht_ui?.temperature_unit == 'F' ? 'tF' : 'tC'
							] == null ||
							status[`temperature:${device.channel}`][
								settings?.ht_ui?.temperature_unit == 'F' ? 'tF' : 'tC'
							] == -999
								? 'ERR'
								: `${mathEpsilon(
										typeof status[`temperature:${device.channel}`] !== 'undefined' &&
											status[`temperature:${device.channel}`][
												settings?.ht_ui?.temperature_unit == 'F' ? 'tF' : 'tC'
											],
										1,
									).toFixed(
										valueFormat,
									)}${settings?.ht_ui?.temperature_unit == 'F' ? ' °F' : ' °C'}`,
					});
				}

				const bleMotionBatteryStatus = {
					name: 'battery',
					icon:
						status?.[`devicepower:${device?.channel}`]?.battery?.percent <= 10
							? 'fa fa-battery-empty'
							: status?.[`devicepower:${device?.channel}`]?.battery?.percent <= 25
								? 'fa-duotone fa-light fa-battery-quarter'
								: 'fa-duotone fa-light fa-battery-full',
					value: `${
						status?.[`devicepower:${device?.channel}`]?.battery.percent
							? status?.[`devicepower:${device?.channel}`]?.battery.percent + ' %'
							: 'N/A'
					} `,
					...(status?.[`devicepower:${device?.channel}`]?.battery?.percent <= 10
						? { iconColor: '--color-danger' }
						: {
								iconStyle: getBatteryIconStyle(
									status?.[`devicepower:${device?.channel}`]?.battery?.percent,
								),
							}),
				};

				interface_data.push(bleMotionBatteryStatus);
				break;
			case 'SBWS-90CM':
				interface_data = weather_station_status_configurator(device, status, 'temperature');
				break;
			case 'SBHT-003C':
			case 'SBHT-103C':
			case 'SBHT-203C':
				let tempUnit = undefined;
				if (device?.ht_ui) {
					valueFormat = device.ht_ui.temperature_unit == 'F' ? 0 : 1;
					tempUnit = device.ht_ui.temperature_unit;
				} else {
					valueFormat = settings?.ht_ui?.temperature_unit == 'F' ? 0 : 1;
					tempUnit = settings?.ht_ui?.temperature_unit;
				}

				const humidityValue = status[`humidity:${device.channel}`]?.rh;
				const temperatureValue =
					typeof status[`temperature:${device.channel}`] !== 'undefined'
						? status[`temperature:${device.channel}`][tempUnit == 'F' ? 'tF' : 'tC']
						: undefined;

				interface_data = [
					{
						name: 'humidity',
						icon: 'fa fa-humidity',
						value: formatSensorValue(humidityValue, (value) => `${mathEpsilon(value, 1)} %`),
					},
					{
						name: 'temperature',
						translation: capFirstChar(
							_format('title_chart_temperature', { default: 'temperature' }),
						),
						icon: 'fa fa-thermometer-three-quarters',
						value: formatSensorValue(
							temperatureValue,
							(value) =>
								`${mathEpsilon(value, 1).toFixed(valueFormat)}${tempUnit == 'F' ? ' °F' : ' °C'}`,
						),
						main: true,
						big_val: true,
					},
				];

				const bleHnTBatteryStatus = {
					name: 'battery',
					icon:
						status?.[`devicepower:${device?.channel}`]?.battery?.percent <= 10
							? 'fa fa-battery-empty'
							: status?.[`devicepower:${device?.channel}`]?.battery?.percent <= 25
								? 'fa-duotone fa-light fa-battery-quarter'
								: 'fa-duotone fa-light fa-battery-full',
					value: `${
						status?.[`devicepower:${device?.channel}`]?.battery.percent
							? status?.[`devicepower:${device?.channel}`]?.battery.percent + ' %'
							: 'N/A'
					} `,
					...(status?.[`devicepower:${device?.channel}`]?.battery?.percent <= 10
						? { iconColor: '--color-danger' }
						: {
								iconStyle: getBatteryIconStyle(
									status?.[`devicepower:${device?.channel}`]?.battery?.percent,
								),
							}),
				};

				interface_data.push(bleHnTBatteryStatus);

				if (status?.['illuminance:0']?.illumination) {
					let illumination = status?.['illuminance:0']?.illumination;
					interface_data.push({
						name: 'illuminance',
						icon: 'fa ' + getIlluminationIcon(illumination),
						value: _format('alarm_state_' + illumination),
					});
				}

				break;
			//#endregion Gen GBLE
			//#region  gen GZ z wave
			case 'QNSH-001P10': //shutter
				if (typeof status?.['Multilevel Switch:1']?.currentValue !== 'undefined') {
					let position = status?.['Multilevel Switch:1']?.currentValue;
					let consumed =
						status?.[`Meter:${device.channel + 1}`]?.['value.Electric_W_Consumed/66049'];
					let consumedKwH = status?.['Meter:0']?.['value.Electric_kWh_Consumed/65537'];
					if (position !== null) {
						interface_data.push({
							name: 'switch',
							icon: position > 0 ? 'fa fa-blinds' : 'fa fa-blinds-raised',
							value: position + ' %',
						});
					}
					if (typeof consumed !== 'undefined') {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-bolt',
							value: consumed + ' W',
						});
					}
					if (
						consumedKwH !== null &&
						consumedKwH !== 0 &&
						typeof consumedKwH !== 'undefined'
					) {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-meter-bolt',
							value: consumedKwH + ' kWh',
						});
					}
				}

				break;
			case 'QNSW-001X16': //1
				//status['Multilevel Switch:1'].currentValue
				//maybe here we can't visualize anything

				// if (
				// 	typeof status?.['Multilevel Switch:1']?.currentValue !== 'undefined'
				// ) {
				// 	let position = status['Multilevel Switch:1'].currentValue;
				// 	if (position !== null) {
				// 		interface_data.push({
				// 			icon: position > 0 ? 'fa fa-blinds' : 'fa fa-blinds-raised',
				// 			value: position + ' %'
				// 		});
				// 	}
				// }

				break;
			case 'QNSW-001P16': //1pm
				//status['Multilevel Switch:1'].currentValue
				if (typeof status?.['Meter:0']?.['value.Electric_W_Consumed/66049'] !== 'undefined') {
					let consumed = status?.['Meter:0']?.['value.Electric_W_Consumed/66049'];
					let consumedKwH = status?.['Meter:0']?.['value.Electric_kWh_Consumed/65537'];

					if (consumed !== null) {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-bolt',
							value: consumed + ' W',
						});
					}
					if (
						consumedKwH !== null &&
						consumedKwH !== 0 &&
						typeof consumedKwH !== 'undefined'
					) {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-meter-bolt',
							value: consumedKwH + ' kWh',
						});
					}
				}
			case 'QNSW-002P16': //2pm
				//status['Multilevel Switch:1'].currentValue
				if (
					typeof status?.[`Meter:${device.channel + 1}`]?.[
						'value.Electric_W_Consumed/66049'
					] !== 'undefined'
				) {
					let consumed =
						status?.[`Meter:${device.channel + 1}`]?.['value.Electric_W_Consumed/66049'];
					let consumedKwH = status?.['Meter:0']?.['value.Electric_kWh_Consumed/65537'];
					if (consumed !== null) {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-bolt',
							value: consumed + ' W',
						});
					}
					if (
						consumedKwH !== null &&
						consumedKwH !== 0 &&
						typeof consumedKwH !== 'undefined'
					) {
						interface_data.push({
							name: 'power',
							icon: 'fas fa-meter-bolt',
							value: consumedKwH + ' kWh',
						});
					}
				}

				break;
			//#endregion

			//#region shedure/zendure
			case 'XPZ-SOLARFLOW':
			case 'XPZ-SOLARFLOW1200':
			case 'XPZ-SOLARFLOW2000':
			case 'XPZ-ACE1500':
			case 'XPZ-AIO2400':
			case 'XPZ-HYPER2000':
			case 'XPZ-HUB1200':
				interface_data = [
					{
						name: 'power',
						main: true,
						icon: 'fas fa-house-day',
						value: convert(status.outputHomePower, 'W'),
					},
					{
						name: 'battery_power',
						icon:
							status.outputPackPower > status.packInputPower
								? 'fas fa-battery-bolt'
								: getBatteryIcon(status.electricLevel),
						value: convert(status?.electricLevel, '%'),
					},
					{
						name: 'solar_power',
						icon: 'fas fa-solar-panel',
						value: convert(status.solarInputPower, 'W'),
					},
				];
				break;
			//#ednregion shedure

			case 'LOQED':
				interface_data = loqedStatusConfigurator(status);
				break;

			case 'S4SN-0A61X': {
				const motionDetected = status?.['motion:0']?.motion ?? false;
				const illuminationValue = status?.['illuminance:0']?.illumination || 'bright';
				const illuminationIcon = 'fas ' + getIlluminationIcon(illuminationValue);

				interface_data = [
					{
						name: 'motion',
						icon: motionDetected ? 'fas fa-sensor-on' : 'fas fa-sensor',
						iconColor: motionDetected ? '--color-danger' : '--color-secondary',
						value: motionDetected
							? get(format)('label_motion_detected', { default: 'Motion detected' })
							: get(format)('label_no_motion', { default: 'No motion' }),
						main: true,
					},
					{
						name: 'illuminance',
						icon: illuminationIcon,
						iconColor: 'color-secondary',
						value: get(format)(`option_motion_mode_${illuminationValue}`, {
							default:
								illuminationValue.charAt(0).toUpperCase() + illuminationValue.slice(1),
						}),
					},
				];
				break;
			}
			case 'S4SN-0U61X':
				const illuminationValue = status?.['illuminance:0']?.illumination || 'bright';
				const illuminationIcon = 'fas ' + getIlluminationIcon(illuminationValue);

				const allZones = Object.keys(status).filter((key) => key.startsWith('presencezone:'));
				const mainZoneKey = 'presencezone:200';

				let totalActiveZones: number;

				// If only main zone exists, check its value
				if (allZones.length === 1 && allZones[0] === mainZoneKey) {
					totalActiveZones = status[mainZoneKey]?.value ? 1 : 0;
				} else {
					// If multiple zones exist, count non-main zones with value=true
					totalActiveZones = allZones.filter(
						(key) => key !== mainZoneKey && status[key]?.value,
					).length;
				}

				interface_data = [
					{
						name: 'illuminance',
						icon: illuminationIcon,
						value: get(format)(`option_motion_mode_${illuminationValue}`, {
							default:
								illuminationValue.charAt(0).toUpperCase() + illuminationValue.slice(1),
						}),
					},
					{
						name: 'zone',
						icon: 'fa fa-vector-square',
						value: totalActiveZones + ' zone' + (totalActiveZones === 1 ? '' : 's'),
					},
				];
				break;

			//#region Camera
			case 'SACM-0A0XX0EU0':
			case 'S1CM-0DXW00':
				interface_data = getCameraStatusLabelList(device, status, settings);
				break;
			//#endregion Camera
			default:
				interface_data = [];
				break;
		}
	} catch (error) {
		console.error('Status pull ERORR:', error);
		console.log('DEVICE ERROR:', device);
	}

	let channel_settings = {
		auto_on_delay: 0,
		auto_on: 0,
		auto_off_delay: 0,
		auto_off: 0,
		schedule: false,
	};

	if (is_gen_2(device) && typeof settings?.['switch:' + device.channel] !== 'undefined') {
		channel_settings = settings['switch:' + device.channel];

		if (channel_settings.auto_on_delay > 0 && channel_settings.auto_on) {
			interface_data.push({
				name: 'timer',
				icon: 'fa fa-timer',
				value: channel_settings.auto_on_delay + 's',
				iconColor: '--color-primary',
				push_to: `/ device / ${device.id} `,
				setting_to_open: 'timer',
			});
		}

		if (channel_settings.auto_off_delay > 0 && channel_settings.auto_off) {
			interface_data.push({
				name: 'timer',
				icon: 'fa-duotone fa-timer',
				value: channel_settings.auto_off_delay + 's',
				iconColor: '--color-lighter',
				push_to: `/ device / ${device.id} `,
				setting_to_open: 'timer',
			});
		}
	} else if (
		typeof settings?.[device?.category?.split('s_')[0] + 's']?.[device?.channel] !== 'undefined'
	) {
		if (device.category === 'emeter') {
			channel_settings = settings['relays'][0];
		} else {
			channel_settings = settings[device.category.split('s_')[0] + 's'][device.channel];
		}

		if (channel_settings.auto_off > 0) {
			interface_data.push({
				name: 'timer',
				icon: 'fa-duotone fa-timer',
				value: channel_settings.auto_off + 's',
				iconColor: '--color-lighter',
				push_to: `/ device / ${device.id} `,
				setting_to_open: 'timer',
			});
		}

		if (channel_settings.auto_on > 0) {
			interface_data.push({
				name: 'timer',
				icon: 'fas fa-timer',
				value: channel_settings.auto_on + 's',
				iconColor: '--color-primary',
				push_to: `/ device / ${device.id} `,
				setting_to_open: 'timer',
			});
		}

		if (channel_settings.schedule && device.type !== 'SHTRV-01') {
			interface_data.push({
				name: 'schedule',
				icon: 'fas fa-calendar-alt',
				value: _format('active'),
				push_to: `/ device / ${device.id} `,
				setting_to_open: 'schedule',
			});
		}
	}
	// if (device.gen === 2) {
	// 	let range_extender = settings?.wifi?.ap?.range_extender?.enable;
	// 	if (range_extender) {
	// 		interface_data.push({
	// 			icon: 'fas fa-signal-stream',
	// 			value: `${ _format('connected_devices') } ${
	// status?.wifi?.ap_client_count;
	// 				}`,
	// 			push_to: `/device/${device.id}`,
	// 			setting_to_open: 'networks'
	// 		});
	// 	}
	// }
	// Plus 1, Plus 1PM, Plus I4 external addon

	if (hasAddon(device)) {
		interface_data = getAddonStatus(device, interface_data, true, true);
		if (isPlusUni(device?.type)) {
			const inputCounterUniStatus = getPlusUniCountStatus(device, [], true)?.[0];
			if (inputCounterUniStatus) {
				interface_data.unshift({
					name: 'frequency',
					icon: inputCounterUniStatus.freqIcon,
					value: inputCounterUniStatus.freq,
				});
				interface_data.unshift({
					name: 'count',
					icon: inputCounterUniStatus.icon,
					value: inputCounterUniStatus.value,
				});
			}
		}
	}

	if (isNightModeEnabled(device, settings)) {
		interface_data.unshift({
			name: 'night_mode',
			icon: 'fas fa-moon',
			value: _format('settings_title_night_mode', { default: 'Night mode' }),
		});
	}

	return normalizeForLayout(interface_data);
};

export const normalizeLabelListIconsToLight = <T extends { icon?: string }>(labelList: T[]): T[] => {
	return labelList.map((label) => ({
		...label,
		icon: label.icon
			? label.icon.replace(/\b(fas|far|fat|fa-solid|fa-regular|fa-thin|fa-light)(?=\s)/g, 'fal')
			: label.icon,
	}));
};

const ext_switch_icons_config = {
	basic: {
		icon: ['fa fa-toggle-off', 'fa fa-toggle-on'],
		text: ['close', 'open'],
	},
	door: {
		icon: ['fa fa-door-closed', 'text-primary fa fa-door-open'],
		text: ['close', 'open'],
	},
	garage: {
		icon: ['fa fa-garage', 'fak fa-light-empty-garage'],
		text: ['close', 'open'],
	},
};

const extract_ext_switch_status = (device, status, settings, interface_data) => {
	if (typeof status.ext_switch !== 'undefined') {
		let swicth_list = [];
		if (typeof status.ext_switch === 'object') {
			swicth_list = Object.values(status.ext_switch);
		} else {
			swicth_list = status.ext_switch;
		}

		const type = ext_switch_icons_config[device?.ext_switch_icon || 'basic'];
		swicth_list.map((s) => {
			interface_data.push({
				name: 'switch',
				icon: type.icon[s.input],
				value: _format(
					`alarm_state_state_${type.text[s.input]}${type.text[s.input] === 'open' ? '' : 'd'}`,
				),
			});
		});
	}

	return interface_data;
};

const format_last_seen_report = (status, icon?) => {
	// We have _updated property in UTC timestamp in seconds
	// and new Date() will return date string with the user machine's timezone
	let date = new Date(status?._updated * 1000);

	const dateISO = date.toISOString();
	const hour = date.getHours();
	const min = date.getMinutes();

	return {
		name: 'last report',
		icon: icon ? icon : 'fas fa-flag-alt',
		value: `${convertDateFormat(dateISO).split(' ')[0]} ${getTimeFormat(hour, min)}`,
	};

	// return {name: 'last',icon:faFlagAlt, value:`${_format("last_report")||'last report'}: ${last_report[2]} ${last_report[1]} ${last_report[3]} ${last_report[4]}`}
};

export const getBatteryIcon = (batteryLevel) => {
	const batteryIcons = [
		{ threshold: 75, icon: 'fa-duotone fa-light fa-battery-full' },
		{ threshold: 50, icon: 'fa-duotone fa-light fa-battery-three-quarters' },
		{ threshold: 25, icon: 'fa-duotone fa-light fa-battery-half' },
		{ threshold: 10, icon: 'fa-duotone fa-light fa-battery-quarter' },
		{ threshold: 0, icon: 'fas fa-battery-empty' },
	];

	return batteryIcons.find(({ threshold }) => batteryLevel >= threshold)?.icon;
};

/**
 * Helper function to get battery icon style based on battery level
 * @param batteryLevel - Battery percentage (0-100)
 * @returns iconStyle string for duotone battery icon
 */
export const getBatteryIconStyle = (batteryLevel: number | null | undefined): string => {
	if (batteryLevel == null) {
		return '--fa-secondary-color: var(--color-danger); --fa-secondary-opacity: 1;';
	}
	if (batteryLevel <= 10) {
		return '--fa-primary-color: var(--color-danger); --fa-secondary-color: var(--color-danger-10); --fa-secondary-opacity: 1;';
	}
	if (batteryLevel <= 25) {
		return '--fa-primary-color: var(--color-warning); --fa-secondary-color: var(--color-warning-10); --fa-secondary-opacity: 1;';
	}
	return '--fa-secondary-color: var(--color-3); --fa-secondary-opacity: 1;';
};

export const checkIsCriticalEvent = (value) => {
	const criticalEvents = [
		_format('prop_gas', { default: 'gas' }),
		_format('smoke_detected'),
		_format('prop_flood', { default: 'Flood' }),
		_format('text_muted', { default: 'muted' }),
	];

	return criticalEvents.some((event) => event.toLowerCase() === value?.toString().toLowerCase());
};

export const isMotionIcon = (icon: string) => {
	if (typeof icon !== 'string') return '';

	if (icon?.includes('fa-person-walking') || icon?.includes('fa-door-open')) {
		return `text-primary ${icon}`;
	}

	return icon;
};

export const getEventBasedOnIcon = (icon: string) => {
	if (typeof icon !== 'string') return '';

	if (icon?.includes('fa-person-walking')) return _format('motion');
	if (icon?.includes('fa-door-open')) return _format(`state_opened`);
	if (icon?.includes('fa-door-closed')) return _format(`state_closed`);
	if (icon?.includes('fa-person')) return _format('status_no');

	return '';
};

//Warning (ts.iliev):
//   This function can be quite heavy for large accounts with BLE devices
export const checkGatewaysBluetooth = (device: Device) => {
	const selected = [];
	const gateways = getAvaliableBleGateways();
	let result: boolean;

	const _cachedLists = get(cachedLists);
	if (device.gen == 'GBLE' && !device.shared) {
		gateways.forEach((gatewayDevice) => {
			const hasBLEInList = _cachedLists?.[gatewayDevice.id]?.[
				'ble.cloudrelay.list'
			]?.addrs.includes(getBluetoothMacFromId(device.id));
			if (hasBLEInList) selected.push(gatewayDevice.id);
		});

		//Check if none of the selected observers are enabled
		result = filterEnabledObserverIds(selected).length <= 0;
	} else {
		result = false;
	}
	return result;
};

export const checkGatewayReportOlderThanDay = (device: Device) => {
	if (!device.shared && device.gen == 'GBLE' && device.category !== 'inputs_reader') {
		const ts = devices_status.getByRealId(device.id)?._updated;
		return Date.now() - ts * 1000 > 86400000;
	} else {
		return false;
	}
};

export const getWorkingModeIcon = (option: string) => {
	switch (option) {
		case 'eco':
			return 'fas fa-leaf';
		case 'sleep':
			return 'fas fa-face-sleeping';
		case 'comfort':
			return 'fas fa-house-user';
		case 'cool':
			return 'fas fa-snowflake';
		case 'dry':
		case 'constant_humidity':
			return 'fas fa-droplet';
		case 'ventilation':
		case 'air_ventilation':
			return 'fas fa-fan';
		case 'heat':
			return 'fas fa-sun';
		case 'floor_heating':
		case 'cloth_dry':
			return 'fas fa-heat';
		case 'boost':
			return 'fas fa-rocket-launch';
		case 'continuous':
			return 'fas fa-repeat';
		case 'auto':
			return 'fas fa-a';
		default:
			return '';
	}
};

/**
 * Get the icon class for the illumination state.
 * @param illumination 'dark', 'twilight', or 'bright'.
 * @returns The icon class for the illumination state.
 */
export const getIlluminationIcon = (illumination: string) => {
	switch (illumination) {
		case 'dark':
			return 'fa-moon';
		case 'twilight':
			return 'fa-sun-haze';
		default:
			return 'fa-sun';
	}
};

export function getButtonText(type: UpdateModeTypes, format: (key: string, opts?: any) => string) {
	if (type === 'update') {
		return format('button_update');
	}

	if (type === 'account_transfer_devices' || type === 'bulk_move_devices_to_another_room') {
		return format('button_transfer', { default: 'Transfer' });
	}

	if (type === 'bulk_delete') {
		return format('button_delete');
	}

	return format('button_change');
}

/**
 * Format a sensor value.
 * @param rawValue The raw value to format.
 * @param formatter The formatter function to use.
 * @returns The formatted value.
 */
const formatSensorValue = (rawValue, formatter) => {
	const hasNoData = rawValue == null || rawValue === -999;

	if (hasNoData) {
		return _format('no_data_yet_msg', { default: 'No data yet' });
	}

	return formatter(rawValue);
};
